import { agreementSourceKindSchema } from './agreement.js';
import { FIELD_KEY_PATTERN, MAX_FIELD_KEY_LENGTH, MAX_OPTION_VALUE_LENGTH } from './field-key.js';
import { pageQuerySchema, parseValidationFailureDetails } from './http.js';
import { DEFAULT_LOCALE, LOCALES, localeCodeSchema, multiLanguageContentSchema } from './i18n.js';
import { TEXT_POLICY_CODES, checkText } from './text-policy.js';
import { z } from 'zod';
/**
 * PRD-003 — check-in flow configuration.
 *
 * Two audiences share this file, and the split matters:
 *
 *  1. **The resolved effective flow** (`resolvedCheckInFlowDTOSchema`) — what a
 *     kiosk reads over the device surface. Every inheritance decision from
 *     PRD-003's "Inheritance rules" table is ALREADY APPLIED server-side: a
 *     kiosk never sees a `parentFlowId`, an overlay screen, or an unresolved
 *     `isRequiredOverride`. It receives a flat, ordered list of screens to
 *     render. A Flutter kiosk app is being built against this shape, so treat
 *     it as a published contract: additive changes only from here. (This slice
 *     is the last subtractive one — it dropped `name`, `source` and a field's
 *     `fieldId` from the kiosk document as configuration the kiosk cannot act
 *     on, before there was a client depending on them.)
 *  2. **The admin CRUD payloads** — what the console sends and reads. These
 *     carry the configuration machinery (scope, lock state, override
 *     provenance) the kiosk must never care about.
 *
 * Tenant-authored screen copy is **data**, not i18n (docs/011, CLAUDE.md
 * non-negotiable 9): it is stored as `MultiLanguageContent` JSONB and travels
 * in these DTOs. The kiosk's own chrome — button labels it invents, error
 * text, "Loading…" — stays in `packages/i18n` and never appears here.
 *
 * **STRICT INBOUND, LENIENT OUTBOUND. Do not "simplify" this into one union,
 * and do not "tighten" the outbound side for symmetry.** The rule applies at
 * BOTH levels — the screen kind and the keys inside a screen:
 *
 *  - **Inbound (admin create/update):** strict discriminated unions of literal
 *    `screenType`s, and `.strict()` on every `config`. An admin typing
 *    `maxLenght` or `SCREEN_TYPE` must get a 400, never a silently dropped rule
 *    or a stored typo. The SPA and the API ship from the same commit, so the
 *    console never has to send or read a key this build does not know.
 *  - **Outbound (the kiosk's resolved document):** lenient about an unknown
 *    `screenType` (see `resolvedCheckInScreenSchema`) AND about an unknown key
 *    inside a screen's `config` (`.passthrough()`), and the catch-all variant
 *    requires no copy at all. More screen types are a certainty (badge
 *    printing, health screening, ID scan) and so are additive config keys
 *    (PRD-008's `documentId`, below). A deployed kiosk build must not reject a
 *    whole flow document the day the server adds either one: strictness on the
 *    outbound side turns every additive change into a fleet-wide lobby outage,
 *    one level down from the `screenType` hole it would otherwise plug.
 *
 * **NO OUTBOUND OBJECT IN THIS FILE IS `.strict()`, so an unknown key NEVER FAILS a
 * read.** That is the availability guarantee, and it holds everywhere with no exception.
 *
 * What an unknown key does INSTEAD splits in two, deliberately, and the split is worth
 * understanding before "make them all `.passthrough()` for consistency" looks like an
 * improvement — it is a regression, and it breaks two tests that say so:
 *
 *  - **`.passthrough()` — PRESERVED — on tenant-authored JSONB and on anything a
 *    catch-all describes.** A screen's `config` (`resolved*ScreenConfigSchema`), a field's `validation`,
 *    a dropdown `option`, `resolvedUnrecognisedScreenSchema` /
 *    `checkInUnrecognisedScreenSchema`, and the resolver-computed envelopes
 *    (`effectiveCheckInFlowDTOSchema`, `flowResolutionDegradationSchema`). These describe
 *    a blob whose keys the TENANT and a FUTURE SERVER own, so a key this build cannot
 *    interpret is exactly the thing worth keeping — a preview surface, a support log line
 *    or a newer co-resident reader can still show it.
 *  - **Plain `z.object` — STRIPPED — on every object that describes a ROW.** The
 *    resolved screens and fields, and the admin DTOs. Here the key space is the SERVER's,
 *    and the stripping is not laziness, it is the ALLOW-LIST that keeps server-owned
 *    columns off the wire: `tenantId` on every admin DTO (docs/008 S1/S7), and `flowId`,
 *    `isLocked`, `parentScreenId`, `origin` and a field's library `fieldId` on the kiosk
 *    document, which the kiosk must never receive and which
 *    `check-in-flow.test.ts` asserts are absent even when handed to the parser. Preserving
 *    them would turn one mapper slip into a configuration leak, and an additive key on a
 *    row is something the SERVER adds — i.e. it arrives with the build that reads it.
 *
 * So: preservation where the keys are not ours, allow-listing where they are. Both are
 * lenient in the only sense rule A cares about — neither one ever rejects a document.
 *
 * A resolved field's `validation` and its dropdown `options` were the last two `.strict()`
 * holdouts, and the reasoning for reversing `validation` in particular is recorded at
 * `resolvedFieldValidationSchema` (short version: Zod cannot fail one key, so the price
 * of the strictness is the whole document, and nothing validates an answer against these
 * rules yet). The Flutter kiosk does not parse with Zod at all, so none of this binds
 * it — it binds the SDK and any TypeScript reader.
 *
 * **"OUTBOUND" INCLUDES THE ADMIN READ PATH.** That is the half that gets forgotten,
 * because a console feels closer to home than a lobby does. It is not: the row a bound
 * would reject is precisely the row an admin opened the editor to FIX, and failing the
 * read leaves them no screen on which to fix anything. So the admin document now gets the
 * same catch-all the kiosk document has for an unknown `screenType`
 * (`checkInUnrecognisedScreenSchema`, the sibling of `resolvedUnrecognisedScreenSchema`) —
 * because locking an admin out of the screen they need to repair is strictly worse than
 * showing them a row they cannot interpret.
 *
 * **THE RULE APPLIES TO BOUNDS, NOT JUST TO KEY NAMES.** A max length, a min
 * length, a regex, a numeric range, a uniqueness or coherence refinement — each
 * one belongs on an admin create/update schema and NEVER on a schema describing
 * what the server will send. Inbound, a bound costs one 400 on one edit; outbound,
 * it turns any row that predates or bypasses it into an availability failure with
 * no tier left to degrade to — and for data shared across locations (the field
 * library) that is every kiosk in the tenant, from one over-long label.
 *
 * **The exhaustive list of what remains on the outbound path**, so that "unbounded" is a
 * claim a reader can check rather than take on trust. Four categories, and nothing else:
 *
 *  1. **Non-emptiness on an IDENTIFIER** — `.min(1)` on `id`, `flowId`, `locationId`,
 *     `parentFlowId`, `parentScreenId`, `revision`, a field's `key`. Every one is a
 *     Prisma-generated key or a regex-constrained key, so no row that exists can
 *     violate it, and a reader that received `''` could not use it for anything anyway.
 *     It is NOT applied to a stored HUMAN-AUTHORED string: `name`, `flowName` and
 *     `locationName` are plain `z.string()` precisely because an empty one is a row an
 *     admin has to be able to see in order to rename.
 *  2. **Invariants over values the SERVER DERIVES rather than reads** — `sortOrder`
 *     ordering and its `min(0)` (re-derived from array position by the mapper),
 *     `screenCount` (a count), `supportedLocales` (computed from the document's own
 *     copy), a non-empty `screens` array (the resolver's fold output, with a tier to
 *     fall back to), and `.datetime()` on `createdAt`/`updatedAt`/`effectiveFrom` (a
 *     `Date.toISOString()`, never a stored string). A stored row cannot reach any of
 *     these without passing through the code that computes them.
 *  3. **Closed enums the DATABASE COLUMN itself enforces** — `fieldType`, and
 *     `screenType` on the five known variants, which is why the catch-all exists beside
 *     them rather than instead of them.
 *  4. **`en` present and non-blank inside `MultiLanguageContent`** — the one residual
 *     constraint over stored data, kept because the documented locale fallback chain
 *     terminates at `en` and a string with no `en` is unrenderable. It is not an
 *     availability risk because the mapper absorbs it below the wire; see
 *     `storedContentSchema`.
 *
 * Each is annotated where it sits. Anything NOT in that list is a bug, not a nuance.
 */
/* -------------------------------------------------------------------------- */
/* Closed sets                                                                */
/* -------------------------------------------------------------------------- */
/**
 * The screen kinds a flow can be built from.
 *
 * Must stay identical — same members, **same order** — to the
 * `VisitorScreenType` Prisma enum in `packages/db/prisma/schema.prisma`. A
 * lock-step test in `packages/db` asserts that. It reads the enum's members
 * from the GENERATED client rather than from the schema file —
 * `Prisma.dmmf.datamodel.enums` via `packages/db/src/testing/prisma-enum-values.ts`
 * — which is the same mechanism `packages/db/src/directory-enums.test.ts` uses
 * to pin `PersonType`/`EmploymentStatus` to `PERSON_TYPES`/
 * `EMPLOYMENT_STATUSES` (./person.ts). Reading the generated client means the
 * test fails if the schema changed and nobody re-ran `prisma generate`, which
 * parsing the `.prisma` text would not catch. It has to live in `packages/db`
 * because this package is browser-safe and may never import the Prisma client
 * to check for itself.
 *
 * Only these five ship in S4. Adding a member later is an `ALTER TYPE ... ADD
 * VALUE` that rewrites no existing row, which is how badge printing, health
 * screening and ID scan are meant to arrive — and why the kiosk-facing
 * document tolerates a `screenType` outside this list (see
 * `resolvedCheckInScreenSchema`). Removing or reordering a member is the
 * expensive direction.
 */
export const SCREEN_TYPES = [
    'FIELD_COLLECTION',
    'PHOTO_CAPTURE',
    'DOCUMENT_SIGNATURE',
    'THANK_YOU',
    'VISITOR_TYPE_SELECTION',
];
export const screenTypeSchema = z.enum(SCREEN_TYPES);
/** Whether a `screenType` read off the wire is one this build knows how to render. */
export function isScreenType(value) {
    return SCREEN_TYPES.includes(value);
}
/**
 * The FIXED SHAPE of every flow (S24). Positions 0, 1 and the last are held by a
 * FIELD_COLLECTION carrying `visitor_name`, a VISITOR_TYPE_SELECTION and a
 * THANK_YOU, in that order; an admin may relabel them but never remove,
 * reorder or retype them. Everything between is `EDITABLE`. The role is DERIVED
 * from composed position + type by the server and reported on the admin screen
 * DTO — it is not a stored column, and it is not `isLocked` (which is the
 * FLOW-12/13 tenant mandate over location overrides, a different thing).
 */
export const SCREEN_STRUCTURAL_ROLES = [
    'NAME_ENTRY',
    'VISITOR_TYPE_SELECTION',
    'THANK_YOU',
    'EDITABLE',
];
export const screenStructuralRoleSchema = z.enum(SCREEN_STRUCTURAL_ROLES);
/** The screen types an admin may ADD to a flow — the fixed-position types are seeded, never added. */
export const ADDABLE_SCREEN_TYPES = [
    'FIELD_COLLECTION',
    'PHOTO_CAPTURE',
    'DOCUMENT_SIGNATURE',
];
export function isAddableScreenType(value) {
    return ADDABLE_SCREEN_TYPES.includes(value);
}
/**
 * The input widget a field collects. Lock-step with the `VisitorFieldType`
 * Prisma enum, same rules as `SCREEN_TYPES` above.
 *
 * This is the *widget*, not the meaning. What a field MEANS is its `key`
 * (`visitor_name`, `host`, `purpose`), which is why FLOW-09's seeded library is
 * eight rows over these seven types rather than eight enum members — a tenant
 * adding "Badge number" is a row, never a migration.
 *
 * `HOST_LOOKUP` is its own type rather than `TEXT` with validation because it
 * resolves against the directory (PRD-005) and the kiosk renders a search, not
 * a text box. The search it renders is
 * `GET /api/device/v1/visits/hosts?q=<query>` — `hostSearchQuerySchema` in
 * ./visit.ts, minimum two characters, returning `{ data: HostSummaryDTO[] }`.
 * That is the only directory endpoint the device surface exposes; a
 * `HOST_LOOKUP` field is unrenderable without it.
 */
export const FIELD_TYPES = [
    'TEXT',
    'EMAIL',
    'PHONE',
    'DROPDOWN',
    'CHECKBOX',
    'DATE',
    'HOST_LOOKUP',
];
export const fieldTypeSchema = z.enum(FIELD_TYPES);
/**
 * Whether a flow is the tenant-wide baseline or belongs to one location.
 *
 * Derived, not stored: it is `locationId === null ? 'TENANT' : 'LOCATION'`.
 * FLOW-03 wants scope legible at a glance in the flow list, and deriving it in
 * `toCheckInFlowDTO()` beats making every consumer re-derive it from a
 * nullable id. There is deliberately no Prisma enum behind this.
 */
export const FLOW_SCOPES = ['TENANT', 'LOCATION'];
export const flowScopeSchema = z.enum(FLOW_SCOPES);
/**
 * Why the resolver picked the flow it picked (PRD-003 "Inheritance rules").
 *
 * Also derived, also no Prisma enum. It exists for the ADMIN preview surface
 * (FLOW-16), which can say *which* rule fired without re-running resolution
 * client-side — the single most confusing thing to debug in an inheritance
 * system is a correct answer with no visible reason.
 *
 * Deliberately NOT on the kiosk wire: `resolvedCheckInFlowDTOSchema` carries no
 * `source`. Which inheritance rule fired is a configuration fact an admin needs
 * and a lobby terminal cannot act on, and this file's contract is that the
 * kiosk sees resolution RESULTS, never resolution DECISIONS.
 */
export const FLOW_RESOLUTION_SOURCES = [
    'TENANT_DEFAULT',
    'TENANT_TYPE_DEFAULT',
    'LOCATION_OVERRIDE',
    'LOCATION_TYPE_OVERRIDE',
];
export const flowResolutionSourceSchema = z.enum(FLOW_RESOLUTION_SOURCES);
/**
 * How a screen on a location flow relates to the tenant baseline (FLOW-14 —
 * "the admin UI visually distinguishes inherited, overridden, and locked
 * screens"). Derived from `parentScreenId` and which flow the row lives on;
 * `isLocked` is a separate boolean because a screen can be both locked and
 * overridden.
 */
export const SCREEN_ORIGINS = ['OWN', 'INHERITED', 'OVERRIDDEN'];
export const screenOriginSchema = z.enum(SCREEN_ORIGINS);
/* -------------------------------------------------------------------------- */
/* Semantic field keys — the meaning half of the field library (FLOW-09)      */
/* -------------------------------------------------------------------------- */
/**
 * The closed set of SEMANTIC field keys the platform understands.
 *
 * `VisitorCheckInField.key` is free text a tenant may choose (`badge_number`,
 * `vehicle_plate`) and most keys mean nothing to the server — they are collected,
 * stored and reported on, and that is all. These five are the exception: they are
 * the keys the check-in RUNTIME reads, so a `Visit` can be created from a
 * configured flow's answers without the server guessing which input was the host
 * picker. A field carrying one of these keys is seeded with `isSystem: true`
 * (FLOW-09), and `isSystem` is what stops an admin repurposing `host` for a
 * free-text box after answers exist under it.
 *
 * Deliberately NOT a Prisma enum and not a column: the marker is the key itself.
 * A sixth semantic key is an entry here plus a seeded row, never a migration —
 * the same reasoning as `FIELD_TYPES` above ("a tenant adding 'Badge number' is a
 * row, never a migration").
 */
export const SYSTEM_FIELD_KEYS = [
    'visitor_name',
    'visitor_email',
    'visitor_company',
    'host',
    'purpose',
];
export const systemFieldKeySchema = z.enum(SYSTEM_FIELD_KEYS);
/**
 * What each semantic key feeds on a check-in submission (`createVisitSchema`).
 *
 * `visitorName` is the one that is not a straight 1:1 with a request field:
 * `createVisitSchema` takes `visitorFirstName` and `visitorLastName` separately
 * because `Person` stores them separately (PRD-005), while a kiosk asks for one
 * "Full name" box — asking a visitor to fill two boxes to satisfy our schema is
 * the tail wagging the dog. The single answer is split on submit, which is the
 * runtime's job and is why this map names the CONCEPT rather than a request key.
 *
 * Total by construction: `satisfies` makes an added `SYSTEM_FIELD_KEYS` member a
 * type error here until it declares what it feeds, so a semantic key can never
 * be introduced with no runtime meaning attached.
 */
export const VISIT_FIELD_TARGETS = [
    'visitorName',
    'visitorEmail',
    'visitorCompany',
    'hostPersonId',
    'purpose',
];
export const SYSTEM_FIELD_VISIT_TARGET = {
    visitor_name: 'visitorName',
    visitor_email: 'visitorEmail',
    visitor_company: 'visitorCompany',
    host: 'hostPersonId',
    purpose: 'purpose',
};
/** Whether a `VisitorCheckInField.key` read from the database is a semantic one. */
export function isSystemFieldKey(key) {
    return SYSTEM_FIELD_KEYS.includes(key);
}
/* -------------------------------------------------------------------------- */
/* Shared primitives                                                          */
/* -------------------------------------------------------------------------- */
/**
 * `multiLanguageContentSchema` with a per-locale length ceiling.
 *
 * The ceiling is applied here rather than in ./i18n.ts so the locale key list
 * stays declared exactly once: this walks `Object.entries` of the already
 * parsed value, so it never names a locale and never needs editing when
 * `LOCALES` grows. Unbounded tenant-authored copy in a JSONB column is a
 * genuine row-bloat hazard, not a style preference.
 */
const boundedContentSchema = (maxCharsPerLocale, multiLine = false) => multiLanguageContentSchema.superRefine((value, ctx) => {
    for (const [locale, text] of Object.entries(value)) {
        if (typeof text !== 'string')
            continue;
        if (text.length > maxCharsPerLocale) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [locale],
                message: 'CONTENT_TOO_LONG',
            });
            continue;
        }
        // Handoff 0009 §1b `displayCopy`: base rules only (control / invisible / joiner / zalgo)
        // — emoji allowed, no character set. VALIDATE ONLY, no normalising transform: the stored
        // copy stays byte-for-byte what the admin typed, as before. Safe to apply here because
        // this schema is admin INPUT only (see the note below) — every stored-flow read goes
        // through `storedContentSchema`, which never validates (§1a rule 12). The length check
        // above keeps its UTF-16 `CONTENT_TOO_LONG` code; code points ≤ UTF-16 units, so the
        // policy's own TEXT_TOO_LONG cannot fire after it.
        const checked = checkText(text, 'displayCopy', {
            max: maxCharsPerLocale,
            multiLine,
            allowEmpty: true,
        });
        if (!checked.ok) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [locale],
                message: checked.code,
                params: { textPolicyCode: checked.code },
            });
        }
    }
});
/**
 * **These three are ADMIN-INPUT ONLY. Never put one on an outbound schema.**
 *
 * A length bound constrains what an admin MAY SAVE. It must never constrain what
 * the server WILL SEND, because the two are not symmetrical: on the way in a
 * rejection costs one 400 on one edit, and the admin retypes; on the way out a
 * rejection costs the whole document, and the document is a lobby. A row that
 * predates the bound, or was written by a seed, a migration or a `$queryRaw`
 * that never went through this schema, then fails a read that has no lower tier
 * to fall back to — and for data SHARED across locations (the field library)
 * that is every kiosk in the tenant at once, from one over-long label.
 *
 * The mapper's own reading is the proof this matters: `dto.ts` parses stored copy
 * with the UNBOUNDED `multiLanguageContentSchema` and only the wire contract was
 * bounded, so a 300-character library label passed every tier of resolution and
 * then failed the document at the last step, identically at every tier.
 *
 * The same asymmetry is why unknown keys are strict inbound and lenient outbound
 * (see the file header). Bounds are not a different rule; they are that rule
 * applied to length and shape instead of to key names.
 */
/** Screen and button copy. Admin input only. */
const titleContentSchema = boundedContentSchema(200);
/** Screen body copy, and the agreement text of a DOCUMENT_SIGNATURE screen. Admin input only. */
const bodyContentSchema = boundedContentSchema(4000, true);
/** Field labels and dropdown option labels. Admin input only. */
const labelContentSchema = boundedContentSchema(120);
/**
 * The OUTBOUND reading of the same tenant copy: the SHAPE (an object of locale
 * strings with `en` present), and none of the bounds.
 *
 * Named rather than used inline so every outbound copy key says out loud that it
 * is deliberately unbounded, and so a future bound cannot be added to "the copy
 * schema" and silently reach the wire.
 *
 * **THE ONE RESIDUAL CONSTRAINT OVER STORED DATA, stated rather than glossed over.**
 * `multiLanguageContentSchema` requires `en` to be present and non-blank
 * (`EN_CONTENT_REQUIRED`), which is a rule about a value the server READS, not one it
 * derives — so by the letter of the rule above it does not belong here. It stays for a
 * reason the alternative cannot satisfy: the locale fallback chain this file publishes
 * terminates at `'en'` because `'en'` is the only locale `MultiLanguageContent`
 * guarantees (./i18n.ts), and a copy object with no `en` has no terminal step, i.e. is
 * unrenderable in the requested locale AND in the fallback.
 *
 * It is not an availability risk because the MAPPER absorbs it below the wire rather
 * than letting it reach the contract: `dto.ts#toMultiLanguageContent` `safeParse`s and
 * degrades a failure to `null` (which every copy key here is nullable to accept), and a
 * field's non-nullable `label` degrades to `{ en: <the field key> }` — an ugly label an
 * admin can see and fix, not a lobby that will not open. Note the direction of that: the
 * constraint is enforced where there is somewhere to degrade TO, exactly as rule A asks.
 * Do not "fix" this by making `en` optional; fix it at the mapper if a case ever escapes.
 */
const storedContentSchema = multiLanguageContentSchema;
/** `YYYY-MM-DD`, for the DATE field bounds. Admin input only — see `titleContentSchema` above. */
const calendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * The ceiling on a TEXT field's configurable `minLength`/`maxLength`, in code points — and the
 * bound `checkInAnswerSchema` puts on every string answer (handoff 0009 §2/§3). An admin form
 * editing a field's `maxLength` clamps to this, never above it.
 */
export const FIELD_TEXT_MAX_LENGTH_CEILING = 4000;
/**
 * Per-field validation rules an ADMIN MAY SAVE, stored in
 * `VisitorCheckInField.validation`. `resolvedFieldValidationSchema` below is the
 * outbound reading of the same column.
 *
 * Deliberately tiny and closed. Everything here must be enforceable by a
 * kiosk offline with no server round trip, which rules out anything
 * lookup-shaped. `pattern` is capped in length because it is compiled by the
 * client — a tenant-authored regex is untrusted input to the renderer.
 */
export const fieldValidationSchema = z
    .object({
    minLength: z.number().int().min(0).max(FIELD_TEXT_MAX_LENGTH_CEILING).optional(),
    maxLength: z.number().int().min(1).max(FIELD_TEXT_MAX_LENGTH_CEILING).optional(),
    pattern: z.string().min(1).max(200).optional(),
    minDate: calendarDateSchema.optional(),
    maxDate: calendarDateSchema.optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.minLength !== undefined &&
        value.maxLength !== undefined &&
        value.minLength > value.maxLength) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxLength'],
            message: 'MAX_LENGTH_BELOW_MIN',
        });
    }
    if (value.minDate !== undefined &&
        value.maxDate !== undefined &&
        value.minDate > value.maxDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxDate'],
            message: 'MAX_DATE_BEFORE_MIN',
        });
    }
});
/**
 * The KIOSK-FACING reading of the same stored rules: same keys, same types, no
 * bounds, no `pattern` regex, no coherence refinement, and an unknown key is
 * KEPT rather than rejected.
 *
 * **This reverses a deliberate earlier call, so here is the reasoning.** The
 * argument for keeping `validation` strict outbound was that a rule an old client
 * silently ignores means accepting an answer the server will reject — a wrong
 * answer is worse than a loud failure. That trade is real but it is priced wrong,
 * in three ways:
 *
 *  1. **The loud failure is not local to the rule.** Zod has no "fail this key"
 *     mode: one unknown rule on one field fails `resolvedCheckInFlowDTOSchema`,
 *     which fails the screen, the document, and every screen the tenant DID
 *     configure. The blast radius of the strictness is a lobby; the blast radius
 *     of the ignored rule is one input on one field.
 *  2. **Nothing rejects an answer today.** There is no submission contract yet —
 *     `POST /api/device/v1/visits` (`createVisitSchema` in ./visit.ts) carries no
 *     answers array and validates nothing against these rules. So the harm being
 *     bought is hypothetical while the outage being risked is not; and when
 *     server-side answer validation does land it will enforce the STORED rules
 *     regardless of what a client understood, which is where that guarantee
 *     belongs.
 *  3. **Passthrough gives the reader the choice strict takes away.** Unknown keys
 *     are preserved, so a client that wants to be conservative can see that a
 *     field carries rules it does not understand and act on it — refuse that one
 *     field, mark the answer for server confirmation, log it for support. Strict
 *     parsing does not enable that decision, it makes it for everyone, and it
 *     picks the worst available outcome.
 *
 * Bounds and the min/max coherence refinement come off for the reason in
 * `titleContentSchema` above: they describe what an admin may save. A kiosk
 * receiving `minLength: 40, maxLength: 2` should ignore an incoherent pair (or
 * treat the field as unvalidated) — it must not refuse to open the lobby.
 */
export const resolvedFieldValidationSchema = z
    .object({
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
    pattern: z.string().optional(),
    minDate: z.string().optional(),
    maxDate: z.string().optional(),
})
    .passthrough();
/**
 * One choice on a DROPDOWN field an ADMIN MAY SAVE. `label` is what the visitor
 * reads at the kiosk; `value` is what the answer is stored as.
 * `resolvedFieldOptionSchema` below is the outbound reading.
 *
 * `value` IS OPTIONAL, AND OMITTING IT IS THE NORMAL CASE — exactly the treatment
 * `createCheckInFieldSchema.key` gets, for exactly the same reason. It is a machine
 * string, it is immutable once an answer has been stored against it, and it is not
 * something an admin has any way to choose well. When it is omitted the server
 * derives it from `label.en` with `deriveOptionValues` (./field-key.js), so the
 * console collects ONE thing per option: the words the visitor will see.
 *
 * It stays accepted rather than being removed because an option's value, once
 * stored, must survive every later edit of that option's label or translations. The
 * detail page's Options card re-sends each existing option's value verbatim for that
 * reason, and tenant provisioning (`default-flow.ts`) states the values its seeded
 * choices are reported under. An explicit value is honoured verbatim.
 */
export const fieldOptionSchema = z
    .object({
    value: z.string().trim().min(1).max(MAX_OPTION_VALUE_LENGTH).optional(),
    label: labelContentSchema,
})
    .strict();
/**
 * The kiosk-facing reading of a stored option: unbounded, and lenient about an
 * unknown key for exactly the reasons the `config` objects are (an option is a
 * plausible place for an additive key — an icon, a disabled flag, a translation
 * hint — and a deployed kiosk must not reject a whole flow the day one appears).
 *
 * `.trim()` is dropped as well as the bounds: on the way in trimming normalises
 * what gets persisted, but on the way out it would silently hand the kiosk a
 * DIFFERENT string from the one stored as the answer, which is how two readers of
 * one row end up disagreeing about what the visitor selected.
 */
export const resolvedFieldOptionSchema = z
    .object({
    value: z.string(),
    label: storedContentSchema,
})
    .passthrough();
/**
 * The kiosk-facing reading of the option list: no cap, and no uniqueness
 * refinement. Duplicate stored values make a submitted answer ambiguous, which is
 * why the admin schema below refuses them — but by the time a row holds them the
 * refusal is retroactive, and spending the whole document on it renders no
 * dropdown at all instead of a dropdown with a repeated choice.
 */
export const resolvedFieldOptionsSchema = z.array(resolvedFieldOptionSchema);
/**
 * `options` an ADMIN MAY SAVE: `[]` for every field type except DROPDOWN.
 *
 * TWO uniqueness rules, and they exist for different people.
 *
 * `label.en` must be unique, case-insensitively, because the label is the ONLY
 * thing the visitor can see. A dropdown offering "Yes" and "Yes" is unanswerable at
 * the kiosk no matter how carefully the two are distinguished underneath, and the
 * visitor cannot report the problem — they just pick one and the answer means
 * nothing. Compared case-insensitively on the trimmed string: "Yes" and "yes " are
 * the same choice to everyone standing in the lobby.
 *
 * `value` must be unique because it is the stored answer, so two options sharing one
 * value makes a submitted answer ambiguous forever. This one only fires for an
 * EXPLICIT value — a derived value is made unique by construction
 * (`deriveOptionValues`), which is why the check skips `undefined` rather than
 * treating it as a colliding entry.
 *
 * The label check runs FIRST so that an admin who typed the same choice twice is
 * told that, rather than being told about a machine string they never typed.
 */
export const fieldOptionsSchema = z
    .array(fieldOptionSchema)
    .max(50)
    .superRefine((options, ctx) => {
    const seenLabels = new Set();
    options.forEach((option, index) => {
        const label = option.label.en.trim().toLocaleLowerCase('en');
        if (seenLabels.has(label)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, 'label', 'en'],
                message: 'DUPLICATE_OPTION_LABEL',
            });
            return;
        }
        seenLabels.add(label);
    });
    const seenValues = new Set();
    options.forEach((option, index) => {
        if (option.value === undefined)
            return;
        if (seenValues.has(option.value)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, 'value'],
                message: 'DUPLICATE_OPTION_VALUE',
            });
            return;
        }
        seenValues.add(option.value);
    });
});
/**
 * Asserts `sortOrder` is unique AND strictly ascending across an already
 * ordered array.
 *
 * Both halves matter. Uniqueness alone would allow `[0, 2, 1]`, where the array
 * order and the `sortOrder` values disagree — a consumer that trusts one and a
 * consumer that trusts the other then render different flows, which is the
 * worst possible failure for a kiosk being built by a separate team. Requiring
 * ascending order makes array position and `sortOrder` provably the same
 * ordering, so either may be trusted.
 */
const assertAscendingSortOrder = (items, ctx, duplicateMessage, unorderedMessage, basePath = []) => {
    const seen = new Set();
    let previous = Number.NEGATIVE_INFINITY;
    items.forEach((item, index) => {
        const path = [...basePath, index, 'sortOrder'];
        if (seen.has(item.sortOrder)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: duplicateMessage });
            return;
        }
        seen.add(item.sortOrder);
        if (item.sortOrder <= previous) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: unorderedMessage });
        }
        previous = item.sortOrder;
    });
};
/** A list of ids whose array order *is* the new order. Ids must be distinct. */
const reorderIdsSchema = z
    .array(z.string().min(1))
    .min(1)
    .max(100)
    .superRefine((ids, ctx) => {
    const seen = new Set();
    ids.forEach((id, index) => {
        if (seen.has(id)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index], message: 'DUPLICATE_ID' });
            return;
        }
        seen.add(id);
    });
});
/**
 * `?flag=true|false` on a query string.
 *
 * NOT `z.coerce.boolean()`: that is `Boolean(string)`, so the literal string
 * `"false"` coerces to `true` and a filter silently inverts. Only the two exact
 * spellings are legal.
 */
const booleanQueryParamSchema = z.enum(['true', 'false']).transform((value) => value === 'true');
/* -------------------------------------------------------------------------- */
/* Screen config — one closed shape per screen type                           */
/* -------------------------------------------------------------------------- */
/**
 * Every `*ScreenConfigSchema` below is the typed reading of the single
 * `VisitorCheckInScreen.config` JSONB column. Keeping the per-type payload
 * nested under `config` rather than flattened onto the screen keeps the
 * column mapping trivial (`config` in, `config` out) and means a new screen
 * type adds one schema here and touches nothing else.
 *
 * Copy knobs are nullable with a `null` default, and `null` means "the kiosk
 * uses its own translated default" — that is the seam between tenant-authored
 * data and the app's i18n bundle.
 *
 * **These are the STRICT, ADMIN-FACING readings.** They are what the create and
 * update payloads validate against, so a misspelled key is a 400. Each one has an
 * unbounded, `.passthrough()` TWIN below (`resolved*ScreenConfigSchema`), and the resolved
 * screens use the twin — so an additive key from a newer server survives an older reader,
 * and an over-long stored label does not fail the document it sits in.
 *
 * **THE ADMIN/RESOLVED SPLIT USED TO BE MISSING HERE, and it was a live rule-A
 * violation, not a theoretical one.** The resolved screens wrapped these very objects in
 * `.passthrough()`, which fixes unknown KEYS and does nothing about BOUNDS — so
 * `titleContentSchema`'s 200-character ceiling on `continueLabel`/`submitLabel` and
 * `autoDismissSeconds`' 1..600 range were on the outbound path, exactly what the rule at
 * `titleContentSchema` forbids. The note that used to sit here argued it was safe because
 * the mapper reads every `config` column through
 * `parseStoredJsonOr(<schema>, row.config, <typed default>)` and so degrades an
 * out-of-bounds config to that screen type's defaults before it reaches the wire. Two
 * things are wrong with that:
 *
 *  1. It made the contract's own claim false, and a contract nobody can trust to mean
 *     what it says is worse than a bound. A test in `check-in-flow.test.ts` now feeds a
 *     5,000-character `submitLabel` to every outbound schema; it FAILED against the
 *     wrapped-admin-schema version, which is how this was found rather than argued.
 *  2. The degradation it relies on is silent and lossy: a tenant's configured
 *     `autoDismissSeconds: 900` came back as `null`, and their Spanish submit button
 *     disappeared. "Not an availability risk" is true and is not the same as "not a bug".
 *
 * Splitting them is a bound removal that CAN be done here alone, because the two readings
 * infer the identical TypeScript type — `boundedContentSchema(n)` and
 * `multiLanguageContentSchema` both yield `MultiLanguageContent`, and dropping
 * `.int().min(1).max(600)` still yields `number`. So no consumer's types move.
 * `apps/api/.../dto.ts` still builds its own `STORED_*_CONFIG` by `.passthrough()`-ing the
 * ADMIN schemas, which keeps the silent-degradation half of (2) alive one layer below the
 * contract; pointing those five constants at the resolved twins below is now a one-line
 * import change in that file.
 */
export const fieldCollectionScreenConfigSchema = z
    .object({
    submitLabel: titleContentSchema.nullable().default(null),
})
    .strict();
/**
 * **NOTHING ACCEPTS A PHOTO YET, so `isRequired: true` cannot be enforced and
 * must not be treated as a gate.** The only device write that exists is
 * `POST /api/device/v1/visits` (`createVisitSchema` in ./visit.ts): typed
 * visitor properties, no image field, no upload URL, no storage reference.
 * There is no endpoint, no `photoUrl` on any DTO, and no S3 presign in the
 * device surface. A PHOTO_CAPTURE screen is therefore renderable and
 * configurable but its output has nowhere to go, and `isRequired` defaults to
 * `true` only so the eventual capture slice does not have to migrate every
 * seeded row from `false`.
 *
 * For the Flutter team: do NOT build a blocking capture step against this.
 * Until a capture endpoint is published in this package, treat the screen as
 * informational (or skip it) — a kiosk that refuses to continue without
 * uploading a photo it cannot upload is a lobby that cannot check anyone in.
 */
export const photoCaptureScreenConfigSchema = z
    .object({
    /** `false` lets a visitor skip the photo; the screen still renders. */
    isRequired: z.boolean().default(true),
    allowRetake: z.boolean().default(true),
    /** `true` shows a button on the kiosk to flip between front and rear camera. */
    showCameraSwitch: z.boolean().default(false),
    /** `true` advances to the next screen (or submits the check-in, if this is the
     * flow's last screen) roughly 1s after a visitor taps capture, with no further
     * tap required. */
    autoAdvanceOnCapture: z.boolean().default(false),
})
    .strict();
/**
 * The agreement text itself is the screen's `body` unless `documentId` names a
 * `VisitorAgreementDocument` (PRD-008 DOC-03), in which case the resolved screen's
 * `agreement` sibling carries that document's CURRENT version instead — see
 * `resolvedAgreementSchema` below.
 *
 * `requiresTypedName`/`requiresDrawnSignature` name what a visitor must supply to accept —
 * `agreementAcceptanceInputSchema.typedName`/`signatureFileId` in ./visit.js — REVERSING
 * the deferral this comment used to record here ("no signature can be submitted yet, typed
 * name only" — S15 Constraint 2). The `.superRefine` below requires at least one of the two
 * to be `true`: a screen requiring neither could never be satisfied by any acceptance.
 *
 * `requiresReadToEnd` is a different kind of flag from those two: it names nothing a visitor
 * SUBMITS — there is no server-side acceptance record for "scrolled to the end" the way there
 * is for a typed name or a drawn signature, and it stays that way (no new column on
 * `VisitorAgreementAcceptance`, no new acceptance-input key in `./visit.js`). It is a
 * display/gate setting the kiosk reads to decide when to enable its own Sign button — purely
 * presentational from the server's point of view, exactly like `PHOTO_CAPTURE`'s
 * `isRequired`/`allowRetake`. Defaults to `false` so every screen written before this key
 * existed keeps rendering with its Sign button enabled immediately, with no migration.
 */
export const documentSignatureScreenConfigSchema = z
    .object({
    /** Require the visitor to type their name to accept. */
    requiresTypedName: z.boolean().default(true),
    /** Require the visitor to draw a finger/touch signature to accept (DOC-05 reversal of
     * S15 Constraint 2). Defaults to `false` so every screen written before this column
     * existed keeps its S15 behavior — typed name only — with no migration. */
    requiresDrawnSignature: z.boolean().default(false),
    /** Keep the kiosk's Sign button disabled until the visitor has scrolled the document to
     * its end. Defaults to `false` — see the doc comment above this schema for why this key
     * carries no acceptance-record obligation. */
    requiresReadToEnd: z.boolean().default(false),
    /**
     * The `VisitorAgreementDocument` whose CURRENT version this screen shows (PRD-008 DOC-03).
     * `null` = an agreement screen an admin has not pointed at a document yet; it renders with
     * the screen's own `body`, exactly as it does today. Optional and defaulted, so every row
     * written before S15 stays valid with no migration (`schema.prisma:1429-1434`).
     */
    documentId: z.string().min(1).nullable().default(null),
})
    .strict()
    .superRefine((value, ctx) => {
    if (!value.requiresTypedName && !value.requiresDrawnSignature) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['requiresDrawnSignature'],
            message: 'TYPED_NAME_OR_DRAWN_SIGNATURE_REQUIRED',
        });
    }
});
export const thankYouScreenConfigSchema = z
    .object({
    /** `null` waits for a tap instead of returning to the idle screen on a timer. */
    autoDismissSeconds: z.number().int().min(1).max(600).nullable().default(null),
})
    .strict();
/** The choices themselves come from the kiosk's check-in bundle, not from the screen. */
export const visitorTypeSelectionScreenConfigSchema = z
    .object({
    promptLabel: titleContentSchema.nullable().default(null),
})
    .strict();
/**
 * The OUTBOUND twins of the five configs above: same keys, same inferred types, no bounds,
 * and `.passthrough()` so an unknown key is preserved rather than rejected or deleted.
 *
 * Declared explicitly rather than derived from the admin objects, and that is the whole
 * point — the previous `kioskConfig(<admin schema>)` helper made the outbound reading a
 * function of the INBOUND one, so every bound an admin schema grew was inherited by the
 * wire automatically and silently. Two schemas that must differ cannot be one schema with
 * a wrapper; the duplication is the price of the guarantee, and it is four keys.
 *
 * Kept honest by construction: `resolved*ScreenConfigSchema` and its admin sibling infer
 * the SAME TypeScript type, so a key added to one and forgotten on the other is a type
 * error at the resolved screen that uses it (its `config` would no longer satisfy what the
 * mapper builds). A bound added to only the admin side — the correct direction — moves
 * nothing.
 *
 * A resolved config carries no `.default()` that the admin side lacks and vice versa:
 * defaults are how a `{}` column (the database default) reads as a full object of platform
 * defaults, and both readings need that identically.
 */
export const resolvedFieldCollectionScreenConfigSchema = z
    .object({
    submitLabel: storedContentSchema.nullable().default(null),
})
    .passthrough();
export const resolvedPhotoCaptureScreenConfigSchema = z
    .object({
    isRequired: z.boolean().default(true),
    allowRetake: z.boolean().default(true),
    showCameraSwitch: z.boolean().default(false),
    autoAdvanceOnCapture: z.boolean().default(false),
})
    .passthrough();
export const resolvedDocumentSignatureScreenConfigSchema = z
    .object({
    requiresTypedName: z.boolean().default(true),
    requiresDrawnSignature: z.boolean().default(false),
    requiresReadToEnd: z.boolean().default(false),
    documentId: z.string().nullable().default(null),
})
    .passthrough();
export const resolvedVisitorTypeSelectionScreenConfigSchema = z
    .object({
    promptLabel: storedContentSchema.nullable().default(null),
})
    .passthrough();
export const resolvedThankYouScreenConfigSchema = z
    .object({
    /**
     * No `.int()`, no `1..600`. A stored `autoDismissSeconds: 900` is a row an admin can
     * see and change; a kiosk handed one should clamp it or ignore it, and must not refuse
     * to render the flow it belongs to. The bound stays on `thankYouScreenConfigSchema`,
     * where the admin is told about it while they can still act on it.
     */
    autoDismissSeconds: z.number().nullable().default(null),
})
    .passthrough();
/* -------------------------------------------------------------------------- */
/* (a) The resolved effective flow — the kiosk contract                       */
/* -------------------------------------------------------------------------- */
/**
 * One field on a resolved FIELD_COLLECTION screen: everything needed to render
 * an input and validate it locally, and nothing else.
 *
 * `isRequired` is already RESOLVED (`ScreenField.isRequiredOverride ??
 * CheckInField.isRequiredByDefault`) — the kiosk never sees the override or the
 * default separately, because a kiosk that had to combine them could disagree
 * with the server about whether a visit is submittable.
 *
 * Two identifiers, and only two:
 *
 *  - `key` is the stable semantic name (`visitor_name`, `host`, `purpose`). It
 *    is what gives a collected answer meaning across flows and across tenants,
 *    and it is what reporting groups by.
 *  - `id` is the `ScreenField` id: this field AT THIS POSITION IN THIS SCREEN.
 *    Use it as the local key for the input widget's state and for local
 *    validation errors — nothing more.
 *
 * **The submission contract is NOT DEFINED YET, and `id` is not (yet) part of
 * it.** Today's only device write is `POST /api/device/v1/visits`
 * (`createVisitSchema` in ./visit.ts), which takes typed visitor properties —
 * `visitorFirstName`, `visitorEmail`, `hostPersonId`, `purpose` — and carries no
 * answers array, no photo and no signature. So do not build a submit payload
 * that echoes `id` or any field the flow declares: how answers for a configured
 * flow are posted back (and whether they key on `id`, on `key`, or on both) is a
 * later slice and will be added to this package when it is decided. Until then,
 * map the five `SYSTEM_FIELD_KEYS` onto `createVisitSchema` via
 * `SYSTEM_FIELD_VISIT_TARGET` below; a non-system field is collected, rendered
 * and validated but has nowhere to go yet.
 *
 * The library entry a field came from (`CheckInField.id`) is not sent: it is an
 * admin-side join key, and a kiosk that had it could do nothing with it.
 *
 * NOTHING HERE IS BOUNDED, and that is deliberate — see `titleContentSchema`.
 * `label` in particular is read back out of a row in the SHARED field library, so
 * a bound here is not a per-location failure, it is every kiosk in the tenant. The
 * `.min(1)`s that remain are non-emptiness on identifiers, not policy: `id` is a
 * Prisma-generated key and `key` is regex-constrained on the way in, so no row that
 * exists can violate them, and a field with no local key is genuinely unrenderable
 * (the kiosk keys widget state on `id`).
 *
 * `fieldType` stays a closed enum — the one bound left on the resolved path — for a
 * different reason: it is a Prisma enum COLUMN, so the database itself refuses a
 * value outside `FIELD_TYPES` and there is no row to be lenient towards. The gap it
 * does leave is an old reader meeting a NEW member (the `screenType` problem), and
 * closing that needs what `screenType` got — a catch-all plus a stated "skip a
 * field type you do not know" client contract — which is a design addition, not a
 * bound removal, and it belongs to the slice that adds the sixth field type.
 */
export const resolvedCheckInFieldSchema = z.object({
    id: z.string().min(1),
    key: z.string().min(1),
    label: storedContentSchema,
    fieldType: fieldTypeSchema,
    isRequired: z.boolean(),
    /** Empty for every type except DROPDOWN. */
    options: resolvedFieldOptionsSchema,
    validation: resolvedFieldValidationSchema,
    sortOrder: z.number().int().min(0),
});
/**
 * Present on every resolved screen. Note what is ABSENT versus the row:
 * `flowId`, `parentScreenId`, `isLocked` and `origin` are configuration
 * concerns the kiosk has no use for, so they are not sent — the same
 * allow-list discipline `visitDTOSchema` applies to `tenantId`/`deviceId`.
 *
 * Copy stays as `MultiLanguageContent` rather than being pre-resolved to one
 * locale so a kiosk that has cached a flow can switch language offline
 * (FLOW-20's fallback is then a local lookup against `defaultLocale`).
 *
 * Copy is nullable in BOTH directions, and `null` means "no tenant copy — the
 * kiosk renders its own translated default for this screen type", exactly like
 * the nullable label knobs in the config objects above. Two reasons it is not
 * required: `multiLanguageContentSchema` rejects a blank `en`, so "this screen
 * has no body text" is otherwise inexpressible (a FIELD_COLLECTION screen
 * usually has none); and `VisitorCheckInScreen.title`/`body` default to `{}` in
 * the database, which is not a valid `MultiLanguageContent` — a kiosk in a
 * lobby must never fail to render a whole flow because one row was written
 * without copy. `toCheckInScreenDTO()` maps `{}` to `null`.
 *
 * Copy is UNBOUNDED here (`storedContentSchema`, not `titleContentSchema`) for the
 * reason spelled out at `titleContentSchema`: the create/update schemas bound what
 * an admin may save, and re-checking the bound on the way back out only converts a
 * row that got past it into a lobby that cannot render. A tenant screen's copy is
 * inherited by every location that does not override it, so the bound would fail
 * every tier at once — and truncating instead is worse still: it silently rewords
 * the text a visitor is being asked to agree to.
 */
const resolvedScreenBaseShape = {
    id: z.string().min(1),
    title: storedContentSchema.nullable(),
    body: storedContentSchema.nullable(),
    sortOrder: z.number().int().min(0),
};
/**
 * WHY EACH `config` BELOW IS A `resolved*ScreenConfigSchema` AND NOT THE ADMIN OBJECT.
 *
 * This is the second half of the forward-compatibility guarantee, and it is not optional
 * decoration. `resolvedCheckInScreenSchema` already stops a whole document failing on an
 * unknown `screenType`; without a lenient `config`, an additive key on a `screenType` the
 * build DOES know — PRD-008's `documentId` on DOCUMENT_SIGNATURE is already promised
 * above, and `printerId`, `retentionDays`, `screeningQuestionIds` are all obvious
 * successors — fails the same document one level down, and every kiosk at every location
 * goes dark on the deploy that adds it.
 *
 * Kept, not stripped, so a preview surface, a support log line or a newer co-resident
 * reader can still see the value. A reader MUST ignore a config key it does not know,
 * exactly as it must skip a screen type it does not know.
 *
 * There used to be a `kioskConfig(<admin schema>)` helper here that produced the outbound
 * reading by `.passthrough()`-ing the inbound one. It is deleted on purpose: it fixed
 * unknown KEYS while silently inheriting every BOUND the admin schema carried, which put a
 * 200-character ceiling on `submitLabel` and a 1..600 range on `autoDismissSeconds` onto
 * the wire. See the note above the config schemas for how that was found and why the
 * explicit twins are not duplication worth removing.
 *
 * The ADMIN create/update payloads keep the `.strict()` originals: a console typo must 400.
 * Strict inbound, lenient outbound.
 */
/**
 * Every variant below is a PLAIN `z.object`, never `.strict()` and deliberately not
 * `.passthrough()` — the row half of the split the file header draws.
 *
 * An unknown key therefore never fails the document (that is the availability guarantee)
 * and is STRIPPED, which is the allow-list that keeps `flowId`, `parentScreenId`,
 * `isLocked` and `origin` off the kiosk wire even if a mapper someday spreads a row into
 * one of these. `check-in-flow.test.ts` hands the parser a screen carrying all three and
 * asserts they are gone, so "make these passthrough for consistency" is a change that
 * fails a test on purpose. The leniency that DOES matter here is one level in and already
 * present: `config` is the `resolved*ScreenConfigSchema` twin, i.e. `.passthrough()` and
 * unbounded, which is where a newer server's additive key actually lands.
 *
 * `.extend()` preserves `unknownKeys`, so the admin variants built from these
 * (`checkInScreenDTOSchema`) inherit the same reading rather than restating it.
 */
export const resolvedFieldCollectionScreenSchema = z.object({
    ...resolvedScreenBaseShape,
    screenType: z.literal('FIELD_COLLECTION'),
    config: resolvedFieldCollectionScreenConfigSchema,
    /**
     * **MAY BE EMPTY, and used to be `.min(1)`.** A FIELD_COLLECTION screen with
     * nothing to collect is a configuration bug rather than an intentional state —
     * but it is a bug an admin creates in one supported click
     * (`createCheckInScreenSchema` defaults `fields` to `[]`: create the screen, get
     * interrupted before attaching questions), and `.min(1)` here spent the WHOLE
     * DOCUMENT on it. Every other screen the tenant configured went dark for one empty
     * one.
     *
     * Emptiness is handled where it can be handled proportionately, and it already
     * is: the resolver drops an empty FIELD_COLLECTION from the fold
     * (`pruneUnrenderableScreens` in the visitor service, logged at `warn`), and if
     * the dropped screen carried a question a check-in needs, resolution falls back
     * a tier rather than serving an unsubmittable flow. A client reading this
     * contract directly should treat a fieldless collection screen the same way it
     * treats a `screenType` it does not know: step over it, do not fail the document.
     *
     * The admin READ shape (`checkInScreenDTOSchema`) already permitted an empty
     * `fields` so a half-built screen stays editable; the two outbound readings of
     * one row now agree, which is the point.
     */
    fields: z
        .array(resolvedCheckInFieldSchema)
        .superRefine((fields, ctx) => assertAscendingSortOrder(fields, ctx, 'DUPLICATE_FIELD_SORT_ORDER', 'FIELDS_NOT_ORDERED')),
});
export const resolvedPhotoCaptureScreenSchema = z.object({
    ...resolvedScreenBaseShape,
    screenType: z.literal('PHOTO_CAPTURE'),
    config: resolvedPhotoCaptureScreenConfigSchema,
});
/**
 * The agreement copy a resolved DOCUMENT_SIGNATURE screen carries, so a kiosk renders the NDA
 * with NO second request — the same construction `resolvedFieldCollectionScreenSchema` uses for
 * `fields`.
 *
 * An ALLOW-LIST (docs/008 S7): the document's admin-facing `name` and the version's
 * `publishedByUserId` are deliberately absent — a lobby tablet renders text, it does not need to
 * know what an admin filed the document under or who last edited it.
 *
 * `title`/`body` are the UNBOUNDED `multiLanguageContentSchema` and NULLABLE: the columns default
 * to `{}` in the database, which is not valid `MultiLanguageContent`, and a version that lost its
 * copy must degrade to "no text" rather than take the lobby down — the same rule
 * `agreementVersionDTOSchema` states.
 *
 * `versionId` is what a kiosk sends back as `documentVersionId` on `POST /visits` in Phase 3;
 * `versionNumber` is what a human reads in an audit.
 *
 * `sourceKind` (DOC-05) is what tells the kiosk WHICH of `title`/`body` and an embedded PDF
 * to render: `TEXT` means the substituted `body` is the whole document; `PDF` means `body`
 * is at most a short summary and the kiosk must additionally fetch
 * `GET /api/device/v1/agreement-versions/:versionId/source-pdf` for the document itself.
 */
export const resolvedAgreementSchema = z.object({
    documentId: z.string().min(1),
    versionId: z.string().min(1),
    versionNumber: z.number().int().positive(),
    title: storedContentSchema.nullable(),
    body: storedContentSchema.nullable(),
    sourceKind: agreementSourceKindSchema,
});
export const resolvedDocumentSignatureScreenSchema = z.object({
    ...resolvedScreenBaseShape,
    screenType: z.literal('DOCUMENT_SIGNATURE'),
    config: resolvedDocumentSignatureScreenConfigSchema,
    /**
     * `.nullable().default(null)` is load-bearing three times over: an unattached screen sends
     * `null`; a kiosk build that strips the key still parses the document (S15 Constraint 3); and
     * `checkInScreenDTOSchema` is built from THIS schema via `.extend(adminScreenExtraShape)`, so
     * the admin read tolerates the mapper not supplying it.
     */
    agreement: resolvedAgreementSchema.nullable().default(null),
});
export const resolvedThankYouScreenSchema = z.object({
    ...resolvedScreenBaseShape,
    screenType: z.literal('THANK_YOU'),
    config: resolvedThankYouScreenConfigSchema,
});
export const resolvedVisitorTypeSelectionScreenSchema = z.object({
    ...resolvedScreenBaseShape,
    screenType: z.literal('VISITOR_TYPE_SELECTION'),
    config: resolvedVisitorTypeSelectionScreenConfigSchema,
});
/**
 * The `screenType` this package reports for a screen kind the reading build does
 * not know. Reserved forever: it can never be added to `SCREEN_TYPES` or to the
 * `VisitorScreenType` Prisma enum, so it cannot collide with a real member.
 */
export const UNRECOGNISED_SCREEN_TYPE = 'UNRECOGNISED';
/**
 * The catch-all wire variant. **Read this before you write the renderer.**
 *
 * # For the client (Flutter, SDK, anything reading the device surface)
 *
 * A client **MUST skip a screen whose `screenType` it does not recognise, and
 * MUST NOT fail the document.** Render the screens you know, in `sortOrder`,
 * and step over the rest. New screen types WILL appear — badge printing, health
 * screening, ID scan are all planned — and they will appear in the middle of a
 * flow, on a server deploy, without any kiosk release. A build that rejects the
 * document on an unknown `screenType` takes every lobby on that build offline
 * the hour the type ships; a build that skips it keeps checking visitors in with
 * the screens it does understand. Skipping is therefore the CONTRACT, not a
 * tolerance: the server is entitled to send you a type you have never seen.
 *
 * A skipped screen is still worth logging locally with its `screenType` — that
 * string is how a support engineer learns a fleet is a release behind.
 *
 * # For TypeScript readers
 *
 * On the wire the unknown kind sits in `screenType` verbatim. Parsing RELABELS
 * it: `screenType` becomes the reserved literal `UNRECOGNISED_SCREEN_TYPE` and
 * the value as it arrived is preserved in `unrecognisedScreenType`. That is not
 * cosmetic. A literal discriminant is what keeps
 * `screen.screenType === 'FIELD_COLLECTION'` narrowing to the typed variant; a
 * `z.string()` discriminant would leave a "could be anything" member in the union
 * for every consumer and collapse `fields` to `unknown`, which is the opposite of
 * the point. It also keeps the whole thing ONE `z.discriminatedUnion`, so a
 * malformed KNOWN screen still reports its real issue at its real path
 * (`screens.0.fields.1.sortOrder`) instead of an opaque `invalid_union`.
 *
 * Unknown keys are PRESERVED here (`.passthrough()`): whatever `config` or extra
 * structure the new type carries is data this build cannot interpret but must
 * not destroy — a preview surface or a log line can still show it.
 *
 * **`title` and `body` may be ABSENT here, not merely `null`.** Every known
 * variant requires the keys (the server always writes them, `null` when there is
 * no tenant copy), but a future screen type is entitled to have no copy concept
 * at all — a badge print, an ID scan, a silent redirect. Requiring `title` on
 * the catch-all would mean `{ id, screenType: 'BADGE_PRINT', sortOrder }` fails
 * the whole document with `title Required`, which is precisely the fleet-wide
 * breakage this variant exists to prevent, one level below `screenType`. Absent
 * parses to `null` (`.default(null)`) rather than to `undefined`, so every parsed
 * screen — known or not — has the same two copy keys and a reader never has to
 * distinguish "no copy" from "no such concept".
 *
 * A KNOWN `screenType` is never relabelled, so a PHOTO_CAPTURE screen with a
 * malformed `config` still fails the parse loudly instead of degrading into
 * "unrecognised". The check is CASE-INSENSITIVE: `'photo_capture'` and
 * `'Photo_Capture'` are a console or fixture bug, not a new screen kind, and
 * relabelling them would
 * silently swallow the mistake — the kiosk would skip a screen it can render.
 * Forward compatibility is for types we have not written yet, never a hiding
 * place for a broken screen we have.
 */
export const resolvedUnrecognisedScreenSchema = z
    .object({
    ...resolvedScreenBaseShape,
    title: storedContentSchema.nullable().default(null),
    body: storedContentSchema.nullable().default(null),
    screenType: z.literal(UNRECOGNISED_SCREEN_TYPE),
    /** The `screenType` exactly as it arrived. Log it; do not switch on it. */
    unrecognisedScreenType: z.string().min(1),
})
    .passthrough();
/**
 * Rewrites `{ screenType: '<something new>' }` into the catch-all's shape before
 * the union sees it. Passed through untouched — so that the union fails loudly —
 * are: anything that is not an object, a known type, a known type in the wrong
 * case, and the reserved label itself. This relabels, it never validates.
 */
const relabelUnrecognisedScreen = (value) => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return value;
    }
    const { screenType, ...rest } = value;
    if (typeof screenType !== 'string') {
        return value;
    }
    // Compared upper-cased: the members of SCREEN_TYPES and the reserved label are
    // all upper snake_case, so this catches a casing mistake on a type we HAVE
    // without ever mistaking a genuinely new type for one.
    const normalised = screenType.toUpperCase();
    if (isScreenType(normalised) || normalised === UNRECOGNISED_SCREEN_TYPE) {
        return value;
    }
    return { ...rest, screenType: UNRECOGNISED_SCREEN_TYPE, unrecognisedScreenType: screenType };
};
/**
 * One screen of the kiosk-facing document: a known type with its exact typed
 * shape, or the forward-compatible catch-all above.
 *
 * The ADMIN WRITE schemas stay STRICT discriminated unions of literals with no
 * catch-all, no relabelling and a `.strict()` `config`
 * (`createCheckInScreenSchema`, `updateCheckInScreenSchema`): the console ships
 * from the same commit as the API, so it never has to send a type or a key it
 * does not know, and a mistyped `screenType` or `config` key from an admin must
 * 400. Strict inbound, lenient outbound. Do not unify them.
 *
 * `checkInScreenDTOSchema` is an admin READ shape built on these resolved
 * variants, so it inherits the lenient `config` — outbound is outbound.
 */
export const resolvedCheckInScreenSchema = z.preprocess(relabelUnrecognisedScreen, z.discriminatedUnion('screenType', [
    resolvedFieldCollectionScreenSchema,
    resolvedPhotoCaptureScreenSchema,
    resolvedDocumentSignatureScreenSchema,
    resolvedThankYouScreenSchema,
    resolvedVisitorTypeSelectionScreenSchema,
    resolvedUnrecognisedScreenSchema,
]));
/** Narrows a parsed screen to the five kinds this build can actually render. */
export function isKnownResolvedScreen(screen) {
    return screen.screenType !== UNRECOGNISED_SCREEN_TYPE;
}
/**
 * `GET /api/device/v1/flows/effective` — the effective flow for the paired
 * device's location. That is the real registered path (see
 * `visitor-surface-routes.ts`); PRD-003 spells it `/api/v1/kiosk/flow`, and the
 * device surface in this codebase is prefixed `/api/device/v1` — see ./device.ts
 * and docs/adr/0009. `GET /api/web/flows/:id/preview` (FLOW-16) returns this same
 * shape nested under `flow` (`flowPreviewDTOSchema`), from the same resolver call, so
 * the admin preview cannot drift from the kiosk.
 *
 * Request: `Authorization: Bearer <device token>`, no query parameters. Which
 * tenant and which location are asked for comes from the token, never from the
 * URL — there is deliberately no `?locationId=` to override it.
 *
 * # Responses
 *
 *  - **200** `{ data: <this shape> }` with `ETag` and `Cache-Control`, below.
 *  - **304** empty body — nothing changed; keep rendering the document you
 *    cached. See the conditional-request rules below.
 *  - **404** `{ error, statusCode }` — no active check-in flow resolves for this
 *    device's location: none assigned, or the only candidate was deactivated
 *    (FLOW-22 soft delete). This is a CONFIGURATION state, not a transport
 *    failure, and it is expected on a freshly created location. A kiosk should
 *    show its own "not set up yet" screen (i18n, `packages/i18n` — there is no
 *    tenant copy to show), keep polling at its normal interval, and NOT discard a
 *    flow it has already cached: an admin mid-edit can produce a transient 404,
 *    and a lobby that forgets a working flow because of one is worse than a lobby
 *    running a slightly stale one.
 *  - **401 / 403** — the device token is missing, expired, or the device is no
 *    longer paired. Re-pair; do not retry in a tight loop.
 *  - **500** `{ error, statusCode }` — a server fault. This is NOT how bad
 *    configuration surfaces: resolution degrades instead of failing, because the
 *    admin write path does not validate flow config yet, so an ordinary admin
 *    edit (an unlocked location override that drops a required placement, or one
 *    with no fields left) must never take a fleet offline. If the folded flow is
 *    not submittable the server falls back a tier — location override → tenant
 *    default → the seeded system default, which is submittable by construction —
 *    and logs the degradation at `warn`. A 500 here therefore means something no
 *    admin action could have caused: a corrupt row, the database unreachable, a
 *    bug. Treat it as transport, not configuration: **keep rendering the flow you
 *    cached, retry with exponential backoff** (a few seconds growing to the
 *    poll interval, jittered, capped — a lobby full of kiosks retrying in step is
 *    a self-inflicted outage), and never discard a cached document over one. With
 *    no cached document, show the same "not set up yet" screen as the 404 path.
 *
 * # Conditional requests — `ETag`, `If-None-Match`, `Cache-Control`
 *
 * This is load-bearing, not an optimisation: a kiosk polls this endpoint to
 * notice configuration changes, and a flow with a dozen screens, per-locale copy
 * and a field library is kilobytes on a link that may be cellular.
 *
 * The rule is ordinary HTTP, and there is exactly one of it:
 *
 *  1. On a 200, **store the `ETag` response header VERBATIM** — bytes as
 *     received, quotes included (it is a quoted strong validator, e.g.
 *     `ETag: "9f2b7c1d0e4a5b63"`).
 *  2. On every later request, send it back **VERBATIM** in `If-None-Match`.
 *  3. Unchanged → 304 with an empty body: keep the cached document. Changed →
 *     200 with a new document and a new `ETag`; replace both.
 *
 * **Never construct `If-None-Match` yourself, and never build it out of
 * `revision`.** `revision` is the bare value; the header is quoted. A client that
 * sends the bare `revision` never matches, so 304 never fires and the endpoint
 * silently degrades to a full download on every poll. Store the header; echo the
 * header.
 *
 * `Cache-Control: private, no-cache` accompanies the 200 and means "you may cache
 * this, but revalidate every time": the document is specific to one device's
 * tenant and location, and a kiosk must never render a flow it has not just
 * confirmed is current.
 *
 * # `revision`
 *
 * Opaque, bare (unquoted), and **not** an `If-None-Match` value. It is the
 * client-side cache key: the name to file the offline copy under, and the value
 * to compare two cached documents by. It is also the token a later slice will use
 * to pin an in-progress visit to the configuration it started on (FLOW-17) — that
 * mechanism is NOT built yet, no submission schema carries a revision today, so do
 * not send one back anywhere. Do not parse it or infer anything from its
 * shape/length: the server derives it and the derivation is free to change.
 *
 * # Locale
 *
 * Copy travels as `MultiLanguageContent`, not pre-resolved to one locale, so a
 * kiosk holding a cached document can switch language offline. The fallback chain
 * for one string is exactly three steps, in order:
 *
 *     requested locale  ->  this document's `defaultLocale`  ->  'en'
 *
 * It terminates at `'en'` because `'en'` is the only locale
 * `MultiLanguageContent` guarantees is present (./i18n.ts); a chain ending
 * anywhere else is unimplementable. `defaultLocale` is the tenant's configured
 * locale, already validated against `LOCALES` server-side and already degraded to
 * `'en'` if it was not one.
 *
 * `supportedLocales` is the set of locales actually AUTHORED on this resolved
 * document's copy — never the platform `LOCALES` list. Offering `es`/`fr` for an
 * English-only flow strands a visitor on screens they cannot read. It always
 * contains `defaultLocale`, and always contains `'en'`.
 *
 * `offeredLocales` IS WHAT THE KIOSK'S SWITCHER IS BUILT FROM, and it is a second
 * array rather than a redefinition of the first because the two answer different
 * questions. `supportedLocales` is a SAFETY property: it must contain `'en'` or the
 * fallback chain above has no terminus, and it must contain `defaultLocale` or its
 * middle step lands nowhere. `offeredLocales` is a PRESENTATION choice, configured
 * per location (`Location.supportedLocales`): a Spanish-only lobby offers one chip
 * and no `EN`, which is precisely a set that may exclude the locale the safety
 * property requires. Collapsing them into one array forces a contradiction — either
 * an `EN` chip nobody in that lobby can read, or a fallback chain terminating
 * nowhere.
 *
 * It is a SUBSET of `supportedLocales` (asserted below), so the switcher can still
 * never offer a locale the document has no copy for; the two are equal whenever a
 * location offers everything its flow is authored in, which is the common case. It
 * is non-empty for the same reason `screens` is: a kiosk with no selectable locale
 * has nothing to render. A client that predates this key falls back to
 * `supportedLocales` and behaves exactly as it did before.
 */
export const resolvedCheckInFlowDTOSchema = z
    .object({
    flowId: z.string().min(1),
    /** The location this was resolved FOR, not necessarily the flow's own scope. */
    locationId: z.string().min(1),
    revision: z.string().min(1),
    /** Second step of the fallback chain above. */
    defaultLocale: localeCodeSchema,
    /** The locales this document has copy in. Always includes `en`. */
    supportedLocales: z.array(localeCodeSchema).min(1),
    /** What the kiosk's switcher offers: the location's languages, narrowed to
     * the ones this document is authored in. May exclude `en`. */
    offeredLocales: z.array(localeCodeSchema).min(1),
    /**
     * Non-empty, and this one STAYS non-empty while the bounds elsewhere come off
     * (see `titleContentSchema`): `screens` is not a stored value being read back,
     * it is the resolver's fold output, and a document with nothing to render is
     * not a degraded flow — it is no flow. The resolver has somewhere to go with
     * that and takes it (`foldCandidate` falls to the next tier on
     * `flow_has_no_renderable_screens`); a kiosk handed `screens: []` has nowhere
     * to go at all. An empty `fields` array, by contrast, costs the reader one
     * skippable screen, which is why that bound did come off.
     */
    screens: z.array(resolvedCheckInScreenSchema).min(1),
})
    // A PLAIN `z.object` — the row/document side of the header's split, so an unknown
    // document-level key is stripped and never rejected.
    //
    // AND A HARD SECOND REASON, recorded so it is not rediscovered as a bug: making this
    // `.passthrough()` pushes the INFERRED type of the two envelopes that nest it
    // (`effectiveCheckInFlowDTOSchema`, `flowPreviewDTOSchema`) past what TypeScript will
    // serialize into a `.d.ts` — `TS7056`, two errors, both on those envelopes — which fails
    // `tsconfig.build.json` and therefore ships NO types at all to the SDK and the console.
    // Measured, not assumed. If this document ever needs true key preservation, the fix is an
    // explicit interface plus an annotated `z.ZodType<...>`, not a `.passthrough()` that
    // silently takes the package's types with it.
    .superRefine((flow, ctx) => {
    assertAscendingSortOrder(flow.screens, ctx, 'DUPLICATE_SCREEN_SORT_ORDER', 'SCREENS_NOT_ORDERED', ['screens']);
    if (!flow.supportedLocales.includes(flow.defaultLocale)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['defaultLocale'],
            message: 'DEFAULT_LOCALE_NOT_SUPPORTED',
        });
    }
    // The terminal step of the fallback chain must exist, or a string with no
    // translation in the requested locale has nowhere to land.
    if (!flow.supportedLocales.includes(DEFAULT_LOCALE)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['supportedLocales'],
            message: 'FALLBACK_LOCALE_NOT_SUPPORTED',
        });
    }
    // A chip for a locale the document has no copy in renders every screen in the
    // fallback locale while claiming to be the picked one — worse than not offering
    // it, because the visitor picked it.
    for (const locale of flow.offeredLocales) {
        if (!flow.supportedLocales.includes(locale)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['offeredLocales'],
                message: 'OFFERED_LOCALE_NOT_AUTHORED',
            });
        }
    }
});
/* -------------------------------------------------------------------------- */
/* Device — the check-in bundle (S24)                                          */
/* -------------------------------------------------------------------------- */
/**
 * One visitor type as the kiosk offers it on the VISITOR_TYPE_SELECTION screen.
 * `label` is the unbounded stored value (rule A); the kiosk resolves it with the
 * same locale fallback it uses for screen copy.
 */
export const resolvedVisitorTypeSchema = z
    .object({
    id: z.string().min(1),
    key: z.string().min(1),
    label: storedContentSchema,
    sortOrder: z.number().int(),
    /** The visitor-type half of the badge-printing AND gate: a badge only prints when
     *  this AND `DeviceBadgeConfigurationDTO.printingEnabled` (the location half) are
     *  both true. The kiosk ANDs the two itself — this field alone never decides. */
    badgePrintingEnabled: z.boolean(),
})
    .passthrough();
/**
 * `GET /api/device/v1/check-in-bundle` — everything a kiosk at one location needs to
 * play a visitor-type-aware check-in, in ONE document.
 *
 * `entryFlow` is the location's resolved "all types" flow: the kiosk plays its first
 * two screens (name, visitor-type choice) before a type is known. After the visitor
 * picks type `T`, the kiosk continues from screen index 2 of
 * `flowsByVisitorType[T]` when that key is present, else from screen index 2 of
 * `entryFlow`. ABSENT KEY MEANS "SAME AS ENTRY" — only types whose resolution lands on
 * a DIFFERENT flow are included, so the polled document stays small.
 *
 * `visitorTypes` lists exactly the active types visible at this location, in kiosk
 * order. A type present here and absent from `flowsByVisitorType` is a normal state.
 *
 * `revision` is the bundle's OWN etag — a hash over the visible type ids and every
 * included flow's `revision` — never one flow's revision reused, or a change to a
 * per-type flow would leave the 304 lying. Same conditional-GET contract as
 * `resolvedCheckInFlowDTOSchema`.
 */
export const checkInBundleDTOSchema = z
    .object({
    locationId: z.string().min(1),
    revision: z.string().min(1),
    visitorTypes: z.array(resolvedVisitorTypeSchema),
    entryFlow: resolvedCheckInFlowDTOSchema,
    flowsByVisitorType: z.record(z.string().min(1), resolvedCheckInFlowDTOSchema),
})
    .passthrough();
/**
 * `GET /api/web/flows/:id/preview` — render a flow as a given location, no `Visit`
 * created (FLOW-16). `visitor.flow.read`.
 *
 * `locationId` IS THE LOCATION, AND NEVER THE TENANT. Unlike the device surface —
 * which derives both from a paired kiosk's token — a preview has no device, so the
 * location has to be asked for. It is checked twice before it is used: against the
 * caller's own `allowedLocationIds('visitor.flow.read')`, and against the token's
 * tenant. Tenant scope still comes only from the token (CLAUDE.md #1).
 *
 * Omitted, it defaults to the flow's own `locationId`; a TENANT-scoped flow has no
 * location to default to, so previewing one is a 400 that names the parameter rather
 * than a guess at which site the admin meant.
 *
 * `locale` DOES NOT NARROW THE DOCUMENT. The resolved document carries every locale
 * it is authored in (see `resolvedScreenBaseShape`) precisely so a kiosk can switch
 * language offline, and the preview must be byte-identical to what the kiosk gets or
 * it is not a preview. `locale` is therefore the locale the CONSOLE intends to render
 * with, and the response answers with the locale the kiosk's documented fallback
 * chain would actually land on (`previewLocale` on `flowPreviewDTOSchema`) — which is
 * the honest answer to "show me this flow in Spanish" for a flow with no Spanish.
 */
export const flowPreviewQuerySchema = z.object({
    /** Which location to resolve inheritance against; the flow's own scope by default. */
    locationId: z.string().min(1).optional(),
    /** Which locale the console intends to render; the tenant's `defaultLocale` by default. */
    locale: localeCodeSchema.optional(),
    /** Which visitor type to resolve for; the flow's own binding (or "all") by default. */
    visitorTypeId: z.string().min(1).optional(),
});
/* -------------------------------------------------------------------------- */
/* (b) Admin — the registered surface, and how a refused save is reported     */
/* -------------------------------------------------------------------------- */
/**
 * # EVERY PATH NAMED BELOW IS REGISTERED AND ANSWERS
 *
 * Twenty flow routes on `visitorAdminRouter`
 * (`apps/api/src/modules/apps/visitor/routes.ts`), all under the `/api/web` prefix:
 * FIVE READS hold `visitor.flow.read` and FIFTEEN WRITES hold `visitor.flow.manage`.
 * Nothing in this file is a planned path any more — the "not registered yet" caveat each
 * write schema carried was deleted in the commit that registered it, which is the rule
 * this note used to state and the reason none of them can quietly outlive its route.
 * (`visitorAdminRouter` also carries two `visitor.visit.*` routes; they belong to
 * ./visit.ts and are not counted here.)
 *
 * ## The five reads — `visitor.flow.read`
 *
 *  - `GET /flows` -> `200 { data: CheckInFlowListItemDTO[], meta }`
 *  - `GET /flows/:id` -> `200 { data: CheckInFlowDetailDTO }`
 *  - `GET /flows/:id/preview` -> `200 { data: FlowPreviewDTO }`
 *  - `GET /fields` -> `200 { data: CheckInFieldDTO[], meta }`
 *  - `GET /locations/:id/flow` -> `200 { data: EffectiveCheckInFlowDTO }`
 *
 * ## The seventeen writes — `visitor.flow.manage`
 *
 * Nine answer `CheckInFlowDetailDTO`, the flow exactly as `GET /flows/:id` would answer
 * it, so a console re-renders the editor from the write's own response without a second
 * round trip: `POST /flows` (`201`), `PATCH /flows/:id` (`200`),
 * `POST /flows/:id/duplicate` (`201`), `POST /flows/:id/screens` (`201`),
 * `PATCH /flows/:id/screens/reorder` (`200`),
 * `PATCH /flows/:id/screens/:screenId` (`200`),
 * `POST /flows/:id/screens/:screenId/fields` (`201`),
 * `PATCH /flows/:id/screens/:screenId/fields/reorder` (`200`),
 * `PATCH /flows/:id/screens/:screenId/fields/:screenFieldId` (`200`).
 *
 * Two answer `CheckInFieldDTO` — the library entry, not a flow:
 * `POST /fields` (`201`) and `PATCH /fields/:id` (`200`).
 *
 * **FOUR ANSWER `204` WITH NO BODY AT ALL**, which the earlier version of this note
 * omitted entirely — it claimed "every response is one of the DTO schemas above", and a
 * client written to that claim waits for a body that is never coming. Named here at their
 * exact method and path, with the refusal each one has of its own:
 *
 *  - `DELETE /flows/:id` — `204`. The hard delete beside FLOW-22's soft one
 *    (`PATCH /flows/:id { isActive: false }`). Both foreign keys are `Restrict`, so a
 *    flow with location overrides is refused `400` naming which
 *    (`FLOW_HAS_OVERRIDES`, `FLOW_IS_ASSIGNED`) rather than cascading.
 *  - `DELETE /flows/:id/screens/:screenId` — `204`. Validated as a PROJECTION: the flow
 *    without that screen is put through the same rules a write is, so the delete is
 *    refused whenever the remainder would break — and, because inheritance means a delete
 *    here changes what OTHER flows resolve to, whenever it would break an ACTIVE
 *    DESCENDANT that inherits the screen. `screens.<screenId>` and `screens` are the keys
 *    to expect.
 *  - `DELETE /flows/:id/screens/:screenId/fields/:screenFieldId` — `204`. Unplaces the
 *    question; the library entry itself stays. Projected the same way, so removing the
 *    last required `host` placement is refused with `SUBMISSION_FIELD_MISSING:host`
 *    rather than quietly leaving a flow that cannot produce a `Visit`.
 *  - `DELETE /fields/:id` — `204`. A system entry
 *    (`SYSTEM_FIELD_CANNOT_BE_DELETED`) or one still placed on any screen (`FIELD_IN_USE`)
 *    is refused, so the library can never be emptied from under a live flow.
 *
 * A `204` still carries the audit row every mutation writes (CLAUDE.md #10) and can still
 * answer `400`, `403` or `404` — the empty body is the SUCCESS case only.
 *
 * ## Where a save is refused
 *
 * `strict inbound, lenient outbound` governs the shapes: the create/update schemas below
 * are strict, bounded discriminated unions, while every response above is one of the DTO
 * schemas — none of them `.strict()`, so no unknown key can fail a read — and unbounded
 * except for the four categories the file header enumerates (identifier non-emptiness,
 * server-derived invariants, database-backed enums, and `en` inside
 * `MultiLanguageContent`). Which of those DTOs preserves an unknown key and which strips
 * it is the header's split; neither ever rejects one.
 *
 * A schema here can only refuse a payload's SHAPE. The refusals that need the stored
 * rows — a `FIELD_COLLECTION` with nothing to collect, a flow that cannot produce a
 * `Visit`, a location override that unlocks or reorders a locked tenant screen, a
 * second active flow at one location, a write that would break an ACTIVE DESCENDANT that
 * inherits from the flow being edited — are enforced by
 * `apps/api/src/modules/apps/visitor/flow-validation.ts` and answer `400
 * VALIDATION_FAILED` with the same `{ formErrors, fieldErrors }` details a failed
 * `safeParse` produces, so an admin console renders both the same way. That file, not
 * this one, is the counterpart to resolution's silent tier-by-tier degradation.
 *
 * **That body is a PUBLISHED contract, not prose**: `validationFailureDetailsSchema`
 * (./http.js) is the envelope, and `FLOW_VALIDATION_CODES` /
 * `parseFlowValidationFailure()` below are the code set and the `fieldErrors` key grammar
 * a builder UI needs to put each refusal next to the control that caused it.
 */
/* -------------------------------------------------------------------------- */
/* (b) Admin — the 400 contract: what a refused save says, in a typed shape   */
/* -------------------------------------------------------------------------- */
/**
 * Every SCREAMING_SNAKE code a refused flow admin request can put in
 * `validationFailureDetails.fieldErrors`, as one closed list. Almost all of them come from
 * a refused WRITE; `LOCATION_REQUIRED` is the one a READ raises, and it is on the list for
 * the same reason as the rest — a console renders it through the same parser.
 *
 * # Keeping it reconciled with the server, in both directions
 *
 * This list is DERIVED, not designed, and it drifted once already: it was written from the
 * refusals that existed at the time, and the refusal `previewFlow` raises through a bare
 * `ValidationError` — rather than through `refuse()` — was never added to it. A code the
 * server raises but this file does not publish is a refusal the builder can only render as
 * a raw string; a code published but no longer raised is a message key nothing will ever
 * reach. Both are invisible until an admin hits one.
 *
 * The server side of the set is exactly three call shapes in
 * `apps/api/src/modules/apps/visitor/` — `refuse(path, code)` and
 * `issues.push({ path, message })` (`service.ts`, `flow-validation.ts`), plus any direct
 * `new ValidationError(..., { formErrors, fieldErrors })` — and every code in the payload
 * groups below is a `message:` on a schema in THIS file. Reconcile against all four when a
 * refusal is added or removed; a template literal (`` `DUPLICATE_${screenType}_SCREEN` ``)
 * expands to one entry per case here.
 *
 * # Why this is published rather than described
 *
 * "Answers 400 with per-field details" is not a contract a UI can be built against. The
 * flow builder has to decide, for each entry in `fieldErrors`, WHICH control to attach the
 * message to and WHAT SENTENCE to show — and both of those are lookups against this list
 * and the path grammar below. Without them a console can only dump raw keys and raw codes
 * next to the form, which is the same "the save was refused and I cannot tell you where"
 * failure the whole save-time validation layer exists to replace. The next slice is that
 * builder; this list is its dependency.
 *
 * # THE SET IS OPEN, and a reader MUST have a default branch
 *
 * `parseFlowValidationFailure()` deliberately types `code` as `string` and reports
 * membership as a separate `isKnownCode` flag rather than narrowing to
 * `FlowValidationCode`, because this is data READ OFF THE WIRE and rule A applies to it
 * exactly as it applies to a flow document: a server one release ahead WILL send a code
 * this build has not heard of, and a client that treats that as a parse failure turns an
 * additive server change into "the console cannot show me why my save was refused".
 * Render an unknown code as its own text — it is a machine key, upper snake_case, and
 * legible enough to be a last resort — and log it. Never switch on it without a default.
 *
 * Codes are also NOT the only thing that appears here. A refusal produced by
 * `safeParse` carries Zod's own English messages (`Required`,
 * `Unrecognized key(s) in object: 'key'`), which are not in this list and never will be:
 * the ones worth showing field-by-field were given explicit codes, and the rest are
 * developer-facing evidence that a console sent a payload it should not have.
 *
 * # i18n
 *
 * These are machine keys, so they are NOT `ERROR_CODES` (./errors.js) and `pnpm
 * i18n:check` does not police them — that check reads `ERROR_CODES` alone. The whole
 * response is one `VALIDATION_FAILED`; these say WHICH rule inside it fired. A console
 * renders them through its own key space, and a code carrying a `:<detail>` suffix
 * (below) supplies the interpolation value.
 */
export const FLOW_VALIDATION_CODES = [
    // ## Payload shape — raised by the `safeParse` of the schemas in THIS file, so a
    // console can hit them without any stored row being consulted.
    'EMPTY_UPDATE',
    'INVALID_FIELD_KEY',
    'EN_CONTENT_REQUIRED',
    'CONTENT_TOO_LONG',
    'MAX_LENGTH_BELOW_MIN',
    'MAX_DATE_BEFORE_MIN',
    'DUPLICATE_OPTION_VALUE',
    'DUPLICATE_OPTION_LABEL',
    'DUPLICATE_ID',
    'DUPLICATE_FIELD',
    'DROPDOWN_REQUIRES_OPTIONS',
    'OPTIONS_NOT_ALLOWED',
    'OVERRIDE_REQUIRES_LOCATION',
    // ## The referenced row is missing, or is not one this write may use. Every one of
    // these is a 400 and not a 404 on purpose: the id came from a BODY, so it is a field of
    // the payload that is wrong, and answering 404 would make the whole save look like a
    // bad URL.
    'FLOW_NOT_FOUND',
    'FIELD_NOT_FOUND',
    'PARENT_FLOW_NOT_FOUND',
    'PARENT_FLOW_FOREIGN_LOCATION',
    'FLOW_INHERITANCE_TOO_DEEP',
    'PARENT_SCREEN_NOT_INHERITED',
    'SCREEN_ALREADY_OVERRIDDEN',
    'SCREEN_TYPE_MISMATCH',
    'SCREEN_DOES_NOT_COLLECT_FIELDS',
    'FIELD_ALREADY_PLACED',
    'UNKNOWN_SCREEN',
    'MISSING_SCREEN',
    'UNKNOWN_SCREEN_FIELD',
    'MISSING_SCREEN_FIELD',
    'FIELD_KEY_TAKEN',
    'FIELD_LABEL_TAKEN',
    'SYSTEM_FIELD_TYPE_IS_FIXED',
    'SYSTEM_FIELD_CANNOT_BE_DELETED',
    'FIELD_IN_USE',
    'FLOW_HAS_OVERRIDES',
    'FLOW_IS_NOT_ACTIVE',
    'LOCATION_ALREADY_HAS_AN_ACTIVE_FLOW',
    // FLOW-01/FLOW-10's "exactly one tenant default" mirrored for the tenant-scoped tier
    // (`locationId: null`) the way `LOCATION_ALREADY_HAS_AN_ACTIVE_FLOW` covers the
    // location-scoped one just above. Keyed `isActive` rather than `locationId`: a
    // tenant-scoped flow's `locationId` is always `null` and immutable after create, so it
    // is not a control an admin can change to fix this — the activation toggle is.
    'TENANT_ALREADY_HAS_AN_ACTIVE_DEFAULT_FLOW',
    // ## Visitor-type binding (S24). A flow is bound to one visitor type or to all
    // (`visitorTypeId: null`); at most one ACTIVE flow per (tenant, location, type).
    'SCOPE_ALREADY_HAS_AN_ACTIVE_FLOW',
    'FLOW_VISITOR_TYPE_NOT_FOUND',
    'VISITOR_TYPE_FOREIGN_LOCATION',
    'PARENT_FLOW_FOREIGN_VISITOR_TYPE',
    'TENANT_SCOPE_REQUIRES_TENANT_ADMIN',
    // ## Fixed structure (S24). Positions 0/1/last are held by the `visitor_name`
    // FIELD_COLLECTION, VISITOR_TYPE_SELECTION and THANK_YOU.
    'DUPLICATE_VISITOR_TYPE_SELECTION_SCREEN',
    'LOCKED_POSITION_SCREEN_MISSING',
    'LOCKED_POSITION_SCREEN_CANNOT_BE_REMOVED',
    'LOCKED_POSITION_SCREEN_CANNOT_BE_REORDERED',
    'LOCKED_POSITION_SCREEN_TYPE_IS_FIXED',
    'NAME_ENTRY_FIELD_CANNOT_BE_REMOVED',
    'SCREEN_TYPE_NOT_ADDABLE',
    // ## Structural — checked on EVERY write, draft or live, because nothing here could
    // ever be correct. The four `LOCKED_SCREEN_*` codes are FLOW-12/13 and are the reason a
    // locked screen can never end up less protected than an unlocked one.
    'DUPLICATE_SCREEN_SORT_ORDER',
    'DUPLICATE_FIELD_SORT_ORDER',
    'DUPLICATE_THANK_YOU_SCREEN',
    'SUBMISSION_REQUIRED_FIELD_CANNOT_BE_OPTIONAL',
    'LOCKED_SCREEN_CANNOT_BE_UNLOCKED',
    'LOCKED_SCREEN_CANNOT_CHANGE_TYPE',
    'LOCKED_SCREEN_CANNOT_BE_REORDERED',
    'LOCKED_SCREEN_REQUIRED_FIELD_MISSING',
    'LOCKED_SCREEN_REQUIRED_FIELD_WEAKENED',
    // ## Submittability — checked only when the write leaves a flow ACTIVE, or leaves an
    // ACTIVE DESCENDANT that inherits from it broken. A draft is exempt (an admin builds in
    // any order and nothing half-finished can reach a lobby, FLOW-22); publishing is the
    // gate. Reported against the flow that OWNS the offending row, which for a descendant
    // failure is not the flow the admin was editing — that is the point of reporting it.
    'EMPTY_FIELD_COLLECTION',
    'FLOW_HAS_NO_RENDERABLE_SCREENS',
    'SUBMISSION_FIELD_MISSING',
    // ## The effect of a write is WIDER THAN THE ROWS IT NAMES. Deleting a screen cascades to
    // every overlay of it (`VisitorCheckInScreen.parentScreenId`, `onDelete: Cascade`), so a
    // descendant flow loses a screen the request never mentioned. Raised against the doomed
    // overlay, under the flow that owns it, and only ALONGSIDE the refusal that says what
    // breaks — it is the "you could not have predicted this" half of that 400, never a refusal
    // on its own. A delete whose descendants all survive is still allowed; the destroyed rows
    // are audited instead.
    'OVERLAY_SCREEN_WOULD_BE_DELETED',
    // ## THE SERVER CANNOT SEE FAR ENOUGH TO PROMISE WHAT THE WRITE WOULD DESTROY. The scan of a
    // flow's overrides is capped (an unbounded read is forbidden), and the cap is chosen to be
    // unreachable — but a flow that reaches it cannot have a cascading screen delete projected
    // honestly: the overlay rows beyond the cap would be destroyed by the foreign key with nothing
    // validated and nothing audited. So the delete is refused instead, keyed to the FLOW rather
    // than a field, because nothing in the payload is wrong — this is our limit, not the tenant's
    // mistake, and it is recoverable in a way a silent partial cascade is not.
    'FLOW_HAS_TOO_MANY_OVERRIDES',
    // ## Raised by a READ, not a write — `GET /api/web/flows/:id/preview`. A TENANT-scoped
    // flow has no location of its own, and inheritance is only defined AGAINST a location, so
    // a preview with no `?locationId=` is refused rather than answered with one site's flow
    // presented as if it were universal. Keyed `locationId`, so a console attaches it to the
    // location picker on the preview pane rather than to the editor form.
    'LOCATION_REQUIRED',
    // ## The free-text policy (handoff 0009) — raised by `boundedContentSchema`'s `displayCopy`
    // check on tenant-authored copy (titles, bodies, labels, option labels), keyed by locale.
    // The SAME six `TEXT_POLICY_CODES` every other policy refusal uses, spread rather than
    // restated so the two lists cannot drift; a console phrases them with the shared
    // text-policy messages, not flow-specific copy.
    ...TEXT_POLICY_CODES,
];
/** Whether a code read off the wire is one this build knows how to phrase. */
export function isFlowValidationCode(value) {
    return FLOW_VALIDATION_CODES.includes(value);
}
/**
 * Separates a code from the one value that makes it actionable —
 * `SUBMISSION_FIELD_MISSING:host`, `LOCKED_SCREEN_REQUIRED_FIELD_WEAKENED:visitor_email`.
 *
 * The detail is a field `key` on every code that carries one today, which turns "your
 * flow was rejected" into "add a host question to it". It is a `string` and not
 * `SystemFieldKey`, because a tenant's own key can appear
 * (`SUBMISSION_REQUIRED_FIELD_CANNOT_BE_OPTIONAL` reports the placement's key) and
 * because the same suffix mechanism is how the next code that needs a value will carry
 * one.
 */
export const FLOW_VALIDATION_DETAIL_SEPARATOR = ':';
/**
 * The id segment standing in for a row the refused write WOULD have created.
 *
 * A create is validated as a projection before anything is written, so the offending
 * screen or placement has no id to name yet. One path scheme covers creates and updates
 * alike — `screens.pending`, `screens.pending.fields.pending:<fieldId>` — and the
 * `pending` marker is itself the signal that NOTHING WAS PERSISTED. A console must not
 * try to look a `pending` id up in the flow it is holding; it belongs to the form the
 * admin just submitted.
 */
export const FLOW_ISSUE_PENDING_ID = 'pending';
/**
 * The first segment marking a refusal about a flow the admin WAS NOT EDITING.
 *
 * `descendantFlows.<flowId>.<path>`, where `<path>` is the ordinary grammar addressing a
 * control inside THAT flow. Published as a constant so the one place a console builds or
 * matches the prefix is this module.
 */
export const FLOW_ISSUE_DESCENDANT_SEGMENT = 'descendantFlows';
/**
 * A `fieldErrors` key -> the control it addresses.
 *
 * TOTAL BY CONSTRUCTION — there is no failure mode and no `null` return. A key this build
 * cannot decompose falls back to `{ kind: 'flow', property: <the key verbatim> }`, which
 * renders at the top of the form instead of nowhere. Same reason the code set is open: a
 * newer server may address something this grammar has no shape for, and the refusal must
 * still be visible.
 *
 * A `descendantFlows.` prefix needs a flow id AND something after it to be that case; the
 * bare prefix and `descendantFlows.<flowId>` alone fall back to the flow-level target,
 * because an id with no control to attach it to has nowhere better to render than the top
 * of the form.
 */
export function parseFlowIssuePath(path) {
    const segments = path.split('.');
    if (segments[0] === FLOW_ISSUE_DESCENDANT_SEGMENT && segments.length > 2) {
        const flowId = segments[1];
        if (flowId !== undefined && flowId !== '') {
            return { kind: 'descendantFlow', flowId, target: parseOwnFlowIssuePath(segments.slice(2)) };
        }
    }
    return parseOwnFlowIssuePath(segments);
}
/** The grammar inside one flow. Takes the segments already split, so nothing re-joins to re-split. */
function parseOwnFlowIssuePath(segments) {
    const path = segments.join('.');
    if (segments[0] !== 'screens' || segments.length === 1 || segments[1] === undefined) {
        return { kind: 'flow', property: path };
    }
    const screenId = segments[1];
    const screenIsPending = screenId === FLOW_ISSUE_PENDING_ID;
    if (segments.length === 2) {
        return { kind: 'screen', screenId, screenIsPending, property: null };
    }
    if (segments[2] === 'fields' && segments.length === 4) {
        const placementId = segments[3] ?? '';
        const [marker, fieldId] = splitOnFirstSeparator(placementId);
        return {
            kind: 'placement',
            screenId,
            screenIsPending,
            placementId,
            placementIsPending: marker === FLOW_ISSUE_PENDING_ID,
            fieldId: marker === FLOW_ISSUE_PENDING_ID ? fieldId : null,
        };
    }
    return {
        kind: 'screen',
        screenId,
        screenIsPending,
        property: segments.slice(2).join('.'),
    };
}
/**
 * A target -> the `fieldErrors` key that addresses it.
 *
 * The inverse of `parseFlowIssuePath`, exported so the grammar has exactly one
 * definition: a caller looking up "did this control get a refusal?" builds the key with
 * this rather than interpolating a template string of its own, which is how the two ends
 * of a dotted-path convention normally drift apart.
 */
export function formatFlowIssuePath(target) {
    switch (target.kind) {
        case 'descendantFlow':
            return `${FLOW_ISSUE_DESCENDANT_SEGMENT}.${target.flowId}.${formatFlowIssuePath(target.target)}`;
        case 'flow':
            return target.property;
        case 'screen':
            return target.property === null
                ? `screens.${target.screenId}`
                : `screens.${target.screenId}.${target.property}`;
        case 'placement':
            return `screens.${target.screenId}.fields.${target.placementId}`;
    }
}
/**
 * `ErrorEnvelope.details` from a refused flow write -> every refusal, decomposed.
 *
 * THE ONE FUNCTION A BUILDER UI NEEDS. `null` when `details` is not a validation-failure
 * body at all (any status other than 400 — `error-middleware.ts` strips `details`
 * everywhere else — or a refusal that predates the shape); a caller that gets `null`
 * falls back to rendering `error` alone and must never read it as success.
 *
 * `safeParse`, never `parse`, and lenient about everything it can be lenient about: the
 * envelope is `.passthrough()`, the code is not narrowed to the published set, and an
 * undecomposable key still yields a form-level target. A 400 a console cannot render is
 * indistinguishable to the admin from a save that vanished.
 */
export function parseFlowValidationFailure(details) {
    const parsed = parseValidationFailureDetails(details);
    if (parsed === null)
        return null;
    const issues = [];
    for (const [path, messages] of Object.entries(parsed.fieldErrors)) {
        const target = parseFlowIssuePath(path);
        for (const message of messages) {
            const [code, detail] = splitOnFirstSeparator(message);
            issues.push({ path, target, message, code, detail, isKnownCode: isFlowValidationCode(code) });
        }
    }
    return { formErrors: parsed.formErrors, issues };
}
/**
 * `'A:B:C'` -> `['A', 'B:C']`, and `'A'` -> `['A', null]`.
 *
 * First separator only, so a detail containing one survives intact — the code half is
 * always the part a `switch` reads.
 */
function splitOnFirstSeparator(value) {
    const at = value.indexOf(FLOW_VALIDATION_DETAIL_SEPARATOR);
    return at === -1
        ? [value, null]
        : [value.slice(0, at), value.slice(at + FLOW_VALIDATION_DETAIL_SEPARATOR.length)];
}
/* -------------------------------------------------------------------------- */
/* (b) Admin — the field library (FLOW-08, FLOW-09)                           */
/* -------------------------------------------------------------------------- */
/**
 * A tenant's reusable field definition. `tenantId` never leaves the server
 * (docs/008 S1/S7).
 *
 * `isSystem` marks the seeded library of FLOW-09. System fields may be
 * referenced and their `isRequiredByDefault` overridden per screen, but their
 * `key` and `fieldType` are the platform's, not the tenant's.
 *
 * Reads the STORED, unbounded, lenient shapes even though it is an admin DTO:
 * outbound is outbound. A row a bound would reject is exactly the row an admin has
 * to see in order to fix it, and failing the list endpoint over it leaves them no
 * screen on which to fix anything. The strict, bounded schemas are on
 * `createCheckInFieldSchema`/`updateCheckInFieldSchema`, where the admin is told
 * about the limit while they can still act on it.
 *
 * A plain `z.object` — never `.strict()`, so an unknown key cannot fail an admin's read of
 * their own library, and stripped rather than kept because THIS key space is the server's
 * and `tenantId` is the key that must never travel (docs/008 S1/S7). The two constraints
 * that stay are both from the header's allow-list: `.min(1)` on `id`/`key` is non-emptiness
 * on an identifier (`key` is regex-constrained inbound and immutable after creation, so no
 * row can hold `''`), and `.datetime()` on the two timestamps describes a
 * `Date.toISOString()` the mapper produces rather than a string it reads. `label` is
 * `storedContentSchema` and NOT `labelContentSchema` — that is the bound this file exists
 * to keep off the library read, because one over-long label here is every kiosk in the
 * tenant.
 */
export const checkInFieldDTOSchema = z.object({
    id: z.string().min(1),
    key: z.string().min(1),
    label: storedContentSchema,
    fieldType: fieldTypeSchema,
    isRequiredByDefault: z.boolean(),
    options: resolvedFieldOptionsSchema,
    validation: resolvedFieldValidationSchema,
    isSystem: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * A DROPDOWN needs choices; nothing else may carry them.
 *
 * The second half is the load-bearing one: options on a CHECKBOX would be
 * silently ignored by the kiosk, and silently-ignored configuration is how an
 * admin concludes the product is broken.
 */
const assertOptionsMatchFieldType = (value, ctx) => {
    if (value.fieldType === undefined || value.options === undefined) {
        return;
    }
    if (value.fieldType === 'DROPDOWN' && value.options.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            message: 'DROPDOWN_REQUIRES_OPTIONS',
        });
    }
    if (value.fieldType !== 'DROPDOWN' && value.options.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            message: 'OPTIONS_NOT_ALLOWED',
        });
    }
};
/**
 * `POST /api/web/fields` — `visitor.flow.manage`.
 *
 * `key` IS OPTIONAL, AND OMITTING IT IS THE NORMAL CASE. It is snake_case, unique
 * per tenant (`@@unique([tenantId, key])`), and immutable after creation — it is
 * the name a collected answer is stored and reported under, so renaming it would
 * orphan every answer already gathered. That is why `key` is absent from the
 * update schema rather than merely discouraged.
 *
 * None of which is an admin's problem. When `key` is omitted the server derives it
 * from `label.en` with `deriveFieldKey` (./field-key.js) and disambiguates a
 * collision with `suffixFieldKey`, so the console never asks for it. The regex is
 * the SAME `FIELD_KEY_PATTERN` the derivation is built to satisfy — one pattern, or
 * the deriver could emit a key this schema refuses.
 *
 * It stays accepted rather than being removed for two callers that genuinely have a
 * key to state: tenant provisioning, which seeds the five `SYSTEM_FIELD_KEYS`
 * (`default-flow.ts`) and whose keys are the platform's semantics rather than a
 * label's spelling, and a test pinning an exact key. An explicit key is honoured
 * verbatim, collision and all — the caller asked for that one.
 */
export const createCheckInFieldSchema = z
    .object({
    key: z
        .string()
        .trim()
        .max(MAX_FIELD_KEY_LENGTH)
        .regex(FIELD_KEY_PATTERN, { message: 'INVALID_FIELD_KEY' })
        .optional(),
    label: labelContentSchema,
    fieldType: fieldTypeSchema,
    isRequiredByDefault: z.boolean().default(false),
    options: fieldOptionsSchema.default([]),
    validation: fieldValidationSchema.default({}),
})
    .superRefine(assertOptionsMatchFieldType);
/**
 * `PATCH /api/web/fields/:id` — `visitor.flow.manage`.
 *
 * `fieldType` is editable only while it is coherent with `options`, and the
 * check here is best-effort by construction: a patch that omits `fieldType`
 * cannot be checked against the stored row from inside a Zod schema. The
 * service completes the invariant against the persisted field — this schema
 * catches the common case at the boundary, it does not replace that check.
 *
 * `.strict()`, SO A PATCH CARRYING `key` IS REFUSED RATHER THAN STRIPPED. The key is
 * immutable (see `createCheckInFieldSchema`) and this schema has never accepted it —
 * but a non-strict object DROPS it silently, so a console that sent a rename got a
 * `200` and a field whose key had not changed. That is the "silently-ignored
 * configuration is how an admin concludes the product is broken" failure this file
 * names two schemas up, applied to the one property whose change would orphan every
 * answer already collected. An `unrecognized_keys` issue names `key` in
 * `fieldErrors`, which is the whole diagnosis.
 */
export const updateCheckInFieldSchema = z
    .object({
    label: labelContentSchema.optional(),
    fieldType: fieldTypeSchema.optional(),
    isRequiredByDefault: z.boolean().optional(),
    options: fieldOptionsSchema.optional(),
    validation: fieldValidationSchema.optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (Object.keys(value).length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'EMPTY_UPDATE' });
    }
    assertOptionsMatchFieldType(value, ctx);
});
/**
 * `POST /api/web/fields/:id/translations` — `visitor.flow.manage`.
 *
 * THE ONLY WAY A NON-`en` LABEL IS OVERWRITTEN. `createField` fills the locales a
 * field has none for, and `updateField` fills only the ones still missing — so an
 * admin who hand-corrected the Spanish label keeps it when someone later reworks
 * the English one. That rule makes a provenance column unnecessary ("has a value"
 * IS the do-not-touch marker), and it also means there has to be an explicit,
 * admin-initiated way to say "no, translate it again". This is that way.
 *
 * `locales` is required and non-empty rather than defaulting to "all of them":
 * regenerating is destructive of an admin's own edit, and a body that has to name
 * what it is about to overwrite cannot do it by accident. `en` is rejected — it is
 * the source, not a target.
 */
export const regenerateFieldTranslationsSchema = z
    .object({
    locales: z
        .array(localeCodeSchema.refine((locale) => locale !== DEFAULT_LOCALE, {
        message: 'SOURCE_LOCALE_NOT_A_TARGET',
    }))
        .min(1)
        .max(LOCALES.length),
})
    .strict();
/**
 * `GET /api/web/fields` — `visitor.flow.read`. The reusable library, paged.
 *
 * NOT LOCATION-SCOPED, because the library is not: one entry is referenced by
 * placements on any number of flows at any number of sites (that is what makes it
 * reusable, FLOW-08). A location-scoped reader therefore sees the whole library —
 * which is exactly the set of questions their own screens can be built from — and
 * `isSystem` is what separates the platform's five semantic keys from the tenant's
 * own additions.
 */
export const checkInFieldListQuerySchema = pageQuerySchema.extend({
    fieldType: fieldTypeSchema.optional(),
    isSystem: booleanQueryParamSchema.optional(),
});
/* -------------------------------------------------------------------------- */
/* (b) Admin — screens and their fields (FLOW-05, FLOW-06, FLOW-07)           */
/* -------------------------------------------------------------------------- */
/**
 * A field as attached to a screen, for the admin editor.
 *
 * Unlike `resolvedCheckInFieldSchema` this shows the requirement decision
 * unresolved as well as resolved: `isRequiredByDefault` (from the library),
 * `isRequiredOverride` (`null` = inherit), and the resulting `isRequired`. The
 * editor needs all three to render "required (inherited)" differently from
 * "required (set here)", and FLOW-13 needs the distinction to reject a location
 * override that tries to relax a locked screen's requirement.
 *
 * Stored, unbounded, lenient shapes for the same reason as `checkInFieldDTOSchema`.
 */
export const screenFieldDTOSchema = z.object({
    id: z.string().min(1),
    fieldId: z.string().min(1),
    key: z.string().min(1),
    label: storedContentSchema,
    fieldType: fieldTypeSchema,
    options: resolvedFieldOptionsSchema,
    validation: resolvedFieldValidationSchema,
    sortOrder: z.number().int().min(0),
    isRequiredByDefault: z.boolean(),
    isRequiredOverride: z.boolean().nullable(),
    isRequired: z.boolean(),
});
/**
 * What the admin DTO adds to the resolved screen: the configuration machinery.
 *
 * The admin screen is deliberately built by extending the resolved variants
 * rather than declared independently — the console and the kiosk then cannot
 * disagree about what a screen *is*, only about how much of it they are shown.
 */
const adminScreenExtraShape = {
    isLocked: z.boolean(),
    origin: screenOriginSchema,
    /** The tenant screen this one overrides, when `origin` is `OVERRIDDEN`. */
    parentScreenId: z.string().min(1).nullable(),
    /** Derived from composed position + type — see `SCREEN_STRUCTURAL_ROLES`. */
    structuralRole: screenStructuralRoleSchema,
};
/**
 * The ADMIN catch-all — the sibling of `resolvedUnrecognisedScreenSchema`, and the reason
 * one unclassifiable row can no longer lock an admin out of the flow it sits in.
 *
 * # The failure this closes
 *
 * `SCREEN_TYPES` is lock-step with a Prisma enum, and the file's own note on that enum
 * says how a new member arrives: `ALTER TYPE ... ADD VALUE`, which rewrites no row and
 * needs no code deploy. So a row whose `screenType` this build has never heard of is a
 * NORMAL state during a rollout, not a corruption. The kiosk document has always survived
 * it; the admin document did not — the union had no catch-all, `parseAdminEnvelope` hard
 * enforces the contract, and the result was a `500` on `GET /api/web/flows/:id`. That is
 * the worst possible place to be strict: the admin cannot open the flow, so they cannot
 * delete or retype the offending screen, so the only surface that could FIX the row is
 * the one the row disables. Showing them a screen they cannot interpret is strictly
 * better, and it is what the kiosk has always done.
 *
 * # What it carries
 *
 * The kiosk catch-all plus the three configuration facts the admin document adds, and all
 * three DEFAULT rather than being required — because the point of a catch-all is to
 * survive a producer this build cannot fully predict, and a required key is the same trap
 * one level down (see the `title`/`body` reasoning on the kiosk variant).
 *
 * The defaults are chosen to FAIL SAFE, never to flatter the row:
 *
 *  - `isLocked` defaults to **`true`**. A screen this build cannot classify is one it
 *    cannot safely offer edit controls for, and rule C — a locked screen must never end
 *    up LESS protected than an unlocked one — makes `false` the one unacceptable guess.
 *    An admin who genuinely needs it gone deletes it; nothing here weakens the write
 *    path, which validates against the stored row and not against this reading.
 *  - `origin` defaults to `OWN` and `parentScreenId` to `null`: the conservative pair. It
 *    claims no override relationship to an ancestor screen that this build cannot verify,
 *    so an editor never draws "overrides the tenant's X" from a guess.
 *
 * # This is NOT sufficient on its own
 *
 * The tolerance lives here, but the row still has to be MAPPED into this shape. The
 * server mapper (`apps/api/src/modules/apps/visitor/dto.ts#toResolvedCheckInScreen`)
 * switches exhaustively over `ScreenType` with no `default`, which at runtime returns
 * `undefined` for an unknown member — so the value that reaches `parseAdminEnvelope` has
 * no `screenType` at all and fails the union on the discriminant instead. Publishing the
 * variant is the half this package owns; emitting it is the mapper's, and the two only
 * close the hole together.
 */
export const checkInUnrecognisedScreenSchema = resolvedUnrecognisedScreenSchema.extend({
    isLocked: z.boolean().default(true),
    origin: screenOriginSchema.default('OWN'),
    parentScreenId: z.string().min(1).nullable().default(null),
    structuralRole: screenStructuralRoleSchema.default('EDITABLE'),
});
/**
 * One screen in the admin editor: a known type with its exact typed shape and the
 * configuration machinery, or the catch-all above.
 *
 * `z.preprocess(relabelUnrecognisedScreen, ...)` is the SAME relabeller the kiosk union
 * uses, deliberately reused rather than reimplemented: a known type in the wrong case
 * still fails loudly on both surfaces, the reserved label still cannot be spoofed, and
 * "which screen types are known" has one definition. A KNOWN `screenType` with a
 * malformed `config` still fails here — forward compatibility is for types not written
 * yet, never a hiding place for a broken screen we have.
 */
export const checkInScreenDTOSchema = z.preprocess(relabelUnrecognisedScreen, z.discriminatedUnion('screenType', [
    resolvedFieldCollectionScreenSchema.extend(adminScreenExtraShape).extend({
        /** May be empty here: a half-built screen must still be readable in the editor. */
        fields: z
            .array(screenFieldDTOSchema)
            .superRefine((fields, ctx) => assertAscendingSortOrder(fields, ctx, 'DUPLICATE_FIELD_SORT_ORDER', 'FIELDS_NOT_ORDERED')),
    }),
    resolvedPhotoCaptureScreenSchema.extend(adminScreenExtraShape),
    resolvedDocumentSignatureScreenSchema.extend(adminScreenExtraShape),
    resolvedThankYouScreenSchema.extend(adminScreenExtraShape),
    resolvedVisitorTypeSelectionScreenSchema.extend(adminScreenExtraShape),
    checkInUnrecognisedScreenSchema,
]));
/**
 * Narrows an admin screen to the five kinds this build can render edit controls for.
 *
 * The admin twin of `isKnownResolvedScreen`, and the guard an editor should reach for
 * before it tries to show a `config` form: the catch-all carries no typed `config` and no
 * `fields`, only enough to say "there is a screen here, at this position, that this
 * console is a release behind on".
 */
export function isKnownCheckInScreen(screen) {
    return screen.screenType !== UNRECOGNISED_SCREEN_TYPE;
}
/**
 * `POST /api/web/flows/:id/screens` — `visitor.flow.manage`.
 *
 * No `sortOrder`: a new screen is appended and moved with the reorder
 * endpoint. Letting a create call name its own position invites two screens
 * claiming one slot, and there is no unique index to stop it (reordering needs
 * transient duplicates, so the schema has none).
 *
 * `config` is optional because every key inside every config has a default —
 * omitting it yields the platform defaults for that screen type.
 *
 * A `title` IS required on the way in even though the DTO tolerates its
 * absence: an admin adding a screen should be made to say what it says, while a
 * kiosk reading a row that predates that rule must still render. `body` is
 * optional because most screens have none.
 */
const createScreenBaseShape = {
    title: titleContentSchema,
    body: bodyContentSchema.nullable().default(null),
    isLocked: z.boolean().default(false),
    /**
     * THE SCREEN IN THE PARENT FLOW THIS ONE REPLACES — the sparse overlay, and the only
     * way to author a location override of an inherited screen (FLOW-11).
     *
     * `null` (the default) adds a screen of the flow's own. A non-null value is legal only
     * on a flow that HAS a parent, and only for a screen somewhere in that flow's
     * inheritance chain; the service refuses anything else, because a `parentScreenId`
     * pointing outside the chain is a screen the fold would keep as one of the child's own
     * (`foldOverlay`'s dangling-overlay rule) — i.e. the same screen rendered twice.
     *
     * `@@unique([flowId, parentScreenId])` stops one flow overlaying the same parent row
     * twice. Without this field the write surface could add screens to an override flow but
     * never override one, which is most of what FLOW-11 is for.
     */
    parentScreenId: z.string().min(1).nullable().default(null),
};
export const createCheckInScreenSchema = z.discriminatedUnion('screenType', [
    z.object({
        ...createScreenBaseShape,
        screenType: z.literal('FIELD_COLLECTION'),
        config: fieldCollectionScreenConfigSchema.default({}),
        /**
         * Array order becomes `sortOrder`, so a duplicate position is not
         * expressible — the reason this takes an ordered list of references rather
         * than objects carrying their own index.
         */
        fields: z
            .array(z
            .object({
            fieldId: z.string().min(1),
            isRequiredOverride: z.boolean().nullable().default(null),
        })
            .strict())
            .max(50)
            .default([])
            .superRefine((fields, ctx) => {
            const seen = new Set();
            fields.forEach((field, index) => {
                if (seen.has(field.fieldId)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [index, 'fieldId'],
                        message: 'DUPLICATE_FIELD',
                    });
                    return;
                }
                seen.add(field.fieldId);
            });
        }),
    }),
    z.object({
        ...createScreenBaseShape,
        screenType: z.literal('PHOTO_CAPTURE'),
        config: photoCaptureScreenConfigSchema.default({}),
    }),
    z.object({
        ...createScreenBaseShape,
        screenType: z.literal('DOCUMENT_SIGNATURE'),
        config: documentSignatureScreenConfigSchema.default({}),
    }),
    z.object({
        ...createScreenBaseShape,
        screenType: z.literal('THANK_YOU'),
        config: thankYouScreenConfigSchema.default({}),
    }),
    z.object({
        ...createScreenBaseShape,
        screenType: z.literal('VISITOR_TYPE_SELECTION'),
        config: visitorTypeSelectionScreenConfigSchema.default({}),
    }),
]);
/**
 * `PATCH /api/web/flows/:id/screens/:screenId` — `visitor.flow.manage`.
 *
 * `screenType` is REQUIRED and is not editable — it is the discriminant that
 * lets `config` stay precisely typed on a partial update, and it doubles as an
 * assertion: the service rejects a patch whose stated type disagrees with the
 * stored row, which catches a console editing a screen that changed underneath
 * it. Changing a screen's type means deleting it and adding the new one, since
 * its `config` and attached fields do not survive the change anyway.
 */
const updateScreenBaseShape = {
    title: titleContentSchema.nullable().optional(),
    body: bodyContentSchema.nullable().optional(),
    isLocked: z.boolean().optional(),
};
const assertNonEmptyScreenUpdate = (value, ctx) => {
    const editable = Object.keys(value).filter((key) => key !== 'screenType');
    if (editable.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'EMPTY_UPDATE' });
    }
};
export const updateCheckInScreenSchema = z
    .discriminatedUnion('screenType', [
    z.object({
        ...updateScreenBaseShape,
        screenType: z.literal('FIELD_COLLECTION'),
        config: fieldCollectionScreenConfigSchema.optional(),
    }),
    z.object({
        ...updateScreenBaseShape,
        screenType: z.literal('PHOTO_CAPTURE'),
        config: photoCaptureScreenConfigSchema.optional(),
    }),
    z.object({
        ...updateScreenBaseShape,
        screenType: z.literal('DOCUMENT_SIGNATURE'),
        config: documentSignatureScreenConfigSchema.optional(),
    }),
    z.object({
        ...updateScreenBaseShape,
        screenType: z.literal('THANK_YOU'),
        config: thankYouScreenConfigSchema.optional(),
    }),
    z.object({
        ...updateScreenBaseShape,
        screenType: z.literal('VISITOR_TYPE_SELECTION'),
        config: visitorTypeSelectionScreenConfigSchema.optional(),
    }),
])
    .superRefine(assertNonEmptyScreenUpdate);
/**
 * `PATCH /api/web/flows/:id/screens/reorder` — `visitor.flow.manage`.
 *
 * The full ordered list, not a move instruction. A whole-list replacement is
 * idempotent and lets the server reject the FLOW-12 violation (a locked screen
 * moved) by comparing the requested order against the locked positions, which
 * a "move screen X to index 3" call cannot express as cleanly.
 */
export const reorderCheckInScreensSchema = z.object({ screenIds: reorderIdsSchema });
/**
 * `POST /api/web/flows/:id/screens/:screenId/fields` — `visitor.flow.manage`.
 */
export const addScreenFieldSchema = z
    .object({
    fieldId: z.string().min(1),
    /** `null` inherits `CheckInField.isRequiredByDefault`. */
    isRequiredOverride: z.boolean().nullable().default(null),
})
    .strict();
/**
 * `PATCH /api/web/flows/:id/screens/:screenId/fields/:screenFieldId` —
 * `visitor.flow.manage`.
 *
 * `isRequiredOverride` is the only mutable property; the field's label, type and
 * validation live in the library and are edited there (FLOW-08 — defined once,
 * reused).
 */
export const updateScreenFieldSchema = z
    .object({ isRequiredOverride: z.boolean().nullable() })
    .strict();
/**
 * `PATCH .../screens/:screenId/fields/reorder` — `visitor.flow.manage`.
 */
export const reorderScreenFieldsSchema = z.object({ screenFieldIds: reorderIdsSchema });
/* -------------------------------------------------------------------------- */
/* (b) Admin — flows (FLOW-03, FLOW-04, FLOW-11, FLOW-21, FLOW-22)            */
/* -------------------------------------------------------------------------- */
/**
 * A flow in the admin list. `tenantId` never leaves the server.
 *
 * `scope` and `screenCount` are derived, not columns: FLOW-03 wants scope and
 * size legible without opening each flow, and computing them in
 * `toCheckInFlowDTO()` keeps that off every consumer.
 *
 * `name` IS PLAIN `z.string()` WITH NO `.min(1)`, which is not an oversight — it is the
 * same call `effectiveCheckInFlowDTOSchema.flowName` already documents, applied here so
 * the two readings of one column agree. `createCheckInFlowSchema` bounds the name to a
 * trimmed 1..120 on the way in, and that is where an admin can act on it; a row that got
 * past it (a seed, a migration, a `$queryRaw`) must still appear in the LIST, because the
 * list is how an admin discovers it exists and the editor is how they rename it. A
 * `.min(1)` here failed a whole page of flows over one of them. `locationName` was
 * already unbounded for the same reason.
 */
export const checkInFlowDTOSchema = z.object({
    id: z.string().min(1),
    name: z.string(),
    scope: flowScopeSchema,
    /** `null` for the tenant-wide default. */
    locationId: z.string().min(1).nullable(),
    locationName: z.string().nullable(),
    /** Set when this flow is a location override of a tenant flow (FLOW-11). */
    parentFlowId: z.string().min(1).nullable(),
    /** The visitor type this flow is bound to; `null` = every visitor type (S24). */
    visitorTypeId: z.string().min(1).nullable(),
    visitorTypeKey: z.string().min(1).nullable(),
    visitorTypeLabel: storedContentSchema.nullable(),
    isLocked: z.boolean(),
    isActive: z.boolean(),
    /** Derived — a count of this flow's own rows, never a stored value being read back. */
    screenCount: z.number().int().min(0),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * `GET /api/web/flows/:id` — the flow with its screens, for the editor.
 * `visitor.flow.read`.
 *
 * `screens` IS THIS FLOW'S OWN STORED ROWS, UNPRUNED AND UNFOLDED — `origin` is
 * therefore only ever `OWN` or `OVERRIDDEN` here, never `INHERITED`. Two reasons,
 * and both are about the editor being able to fix what is broken:
 *
 *  - The resolver PRUNES (an empty FIELD_COLLECTION screen is dropped) and DEGRADES
 *    (an unsubmittable flow falls to the next tier). Both are right for rendering a
 *    lobby and fatal for editing: the screen an admin has to finish is precisely the
 *    one the resolver throws away, and a detail endpoint that hides it leaves them
 *    no screen on which to fix anything.
 *  - Composing the inherited set here would be a SECOND fold implementation beside
 *    the resolver's, and two folds that disagree is the failure this file's
 *    "the console and the kiosk cannot disagree about what a screen is" note exists
 *    to prevent.
 *
 * The composed, inheritance-resolved reading — where `INHERITED` lives — is what
 * `GET /api/web/flows/:id/preview` and `GET /api/web/locations/:id/flow` return, from
 * the resolver itself.
 */
export const checkInFlowDetailDTOSchema = checkInFlowDTOSchema.extend({
    screens: z.array(checkInScreenDTOSchema),
    /**
     * THE FLOW CURRENTLY HOLDING THIS FLOW'S SCOPE, if it is not this one.
     *
     * The scope is the same one FLOW-11 and FLOW-01/FLOW-10 are enforced over: flows sharing
     * this flow's `locationId`, or — when `locationId` is `null` — the tenant-default tier
     * (`locationId: null, parentFlowId: null`). This flow itself is always excluded, so the
     * field is `null` in the two cases that mean "activating changes nothing else": this flow
     * IS the active one, and nothing in the scope is active at all.
     *
     * IT EXISTS SO THE CONSOLE CAN STATE THE CONSEQUENCE BEFORE THE CLICK. Without it the
     * only way to discover that publishing would collide is to attempt it and read
     * `LOCATION_ALREADY_HAS_AN_ACTIVE_FLOW` / `TENANT_ALREADY_HAS_AN_ACTIVE_DEFAULT_FLOW`
     * back off a `400` — a refusal is a poor substitute for a label, and it cannot render
     * "Make active (replaces Reception 2024)" on a button an admin has not pressed yet.
     *
     * `{ id, name }` and not the whole flow: the caller is labelling a button and following a
     * link, and returning a second full detail row here would put a flow's screens in a
     * response about a different flow — one more place for two readings of one row to drift.
     */
    activeSiblingFlow: z.object({ id: z.string(), name: z.string() }).nullable(),
});
/**
 * `POST /api/web/flows/:id/activate` — SWAPPING WHICH FLOW IS LIVE, IN ONE REQUEST.
 *
 * WHY THIS IS A VERB AND NOT `PATCH { isActive: true }`. "Exactly one active flow per
 * scope" is enforced on every write, so promoting flow B while flow A holds the scope is
 * two PATCHes — unpublish A, publish B — and BETWEEN THEM THE SCOPE HAS NO ACTIVE FLOW.
 * Every kiosk resolving through that tier 404s or silently degrades to the tier above for
 * as long as the admin's second request takes, and if it never lands (a validation refusal
 * on B, a closed laptop, a dropped connection) the lobby simply stays dark. A flag on a
 * PATCH cannot fix that: the request that turns B on has to be the same request that turns
 * A off, or there is a window. So the atomicity is the endpoint's whole reason to exist,
 * and naming it `activate` is what makes that promise legible in the route table.
 *
 * It is NOT a way around the publish gate. Activation resolves exactly one conflict — that
 * someone else holds the scope — and every submittability rule `PATCH { isActive: true }`
 * applies still runs, with the same issue codes on the same `400`. A flow a kiosk could not
 * check a visitor in with is refused here too, and the flow that was live stays live.
 *
 * `replacedFlow` is the flow this call DEACTIVATED, or `null` when the scope was already
 * this flow's (a no-op activation) or empty. It is returned rather than left for the caller
 * to infer, because "Reception 2024 is no longer live" is a thing the admin who caused it is
 * owed in the response that caused it, not on their next list refresh.
 */
export const activateCheckInFlowResultSchema = z.object({
    flow: checkInFlowDetailDTOSchema,
    replacedFlow: z.object({ id: z.string(), name: z.string() }).nullable(),
});
/**
 * `POST /api/web/flows` — `visitor.flow.manage`.
 *
 * `locationId` in the body is fine and is not the tenant-isolation hazard:
 * `tenantId` comes from the verified token and never from a payload
 * (CLAUDE.md non-negotiable 1), while the location is a legitimate choice the
 * admin is making — the service still verifies the location belongs to the
 * token's tenant.
 *
 * A `parentFlowId` without a `locationId` is rejected: inheriting from a parent
 * is what makes a flow a *location override* (PRD-003 "Inheritance rules"), so
 * a tenant-scoped flow with a parent is a contradiction, not a variant.
 *
 * `isActive` DEFAULTS TO FALSE: A NEW FLOW IS A DRAFT, and `PATCH /flows/:id
 * { isActive: true }` is the publish gate. This is the shape of the save-time
 * validation PRD-003 asks for ("rejected at save time") — activating a flow, and every
 * later write to one that is already active, must leave it something a kiosk can
 * actually check a visitor in with, so the full submittability check runs there. A
 * brand-new flow has no screens at all and could therefore never pass it: defaulting
 * to active would make `POST /flows` answer `400` every single time, and exempting the
 * create call from the check would just move the wall to the first
 * `POST /flows/:id/screens`. A draft has no such wall — an admin builds it in any
 * order, and nothing they have half-finished can reach a lobby, because resolution
 * only ever selects an active flow (FLOW-22).
 *
 * `isActive: true` is still accepted here, and is still checked: it means "publish this
 * as you create it", which only succeeds for a flow that needs no screens of its own —
 * a location override inheriting a submittable parent.
 */
export const createCheckInFlowSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    locationId: z.string().min(1).nullable().default(null),
    visitorTypeId: z.string().min(1).nullable().default(null),
    parentFlowId: z.string().min(1).nullable().default(null),
    isActive: z.boolean().default(false),
})
    .superRefine((value, ctx) => {
    if (value.parentFlowId !== null && value.locationId === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['locationId'],
            message: 'OVERRIDE_REQUIRES_LOCATION',
        });
    }
});
/**
 * `PATCH /api/web/flows/:id` — `visitor.flow.manage`.
 *
 * Scope is immutable: `locationId` and `parentFlowId` are absent on purpose.
 * Re-pointing a live flow at another location would silently change what
 * visitors at two sites are asked, with no record that it happened; duplicate
 * (FLOW-21) and activate (FLOW-22) are the supported path.
 *
 * `isActive: false` is the FLOW-22 soft delete — a deactivated flow is excluded
 * from resolution but keeps its history.
 */
export const updateCheckInFlowSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    isLocked: z.boolean().optional(),
    isActive: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'EMPTY_UPDATE' });
/**
 * `POST /api/web/flows/:id/duplicate` — FLOW-21, `visitor.flow.manage`.
 *
 * THE COPY IS ALWAYS A DRAFT (`isActive: false`), which is why there is no `isActive`
 * here to ask otherwise. A duplicate of a location's flow that arrived active would be
 * a SECOND active flow at that location, which is the invariant
 * `flow-validation.ts` refuses — and resolution's location tier would pick the newest
 * of the two, silently repointing that lobby at an unedited copy the moment it was
 * made. `parentFlowId` is copied from the source, so duplicating a location override
 * produces another override of the same tenant flow rather than a fork of it.
 */
export const duplicateCheckInFlowSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    /** `null` copies as another tenant-scoped flow. */
    locationId: z.string().min(1).nullable().default(null),
    /** `null` copies as a flow for every visitor type. */
    visitorTypeId: z.string().min(1).nullable().default(null),
})
    .strict();
/**
 * `GET /api/web/flows` — `visitor.flow.read`. Returns `checkInFlowListItemDTOSchema`.
 *
 * LOCATION-SCOPED LIKE `GET /api/web/visits`, WITH ONE ADDITION: a location-scoped
 * reader sees their own locations' flows AND every tenant-scoped flow
 * (`locationId: null`). The tenant flow is not someone else's configuration to them —
 * it is the baseline their own screens inherit from, and hiding it would make
 * `origin: 'OVERRIDDEN'` on their own screens reference a flow they cannot see.
 * `?locationId=` narrows within that scope and never past it (`resolveLocationFilter`
 * in the visitor service).
 */
export const checkInFlowListQuerySchema = pageQuerySchema.extend({
    scope: flowScopeSchema.optional(),
    locationId: z.string().min(1).optional(),
    visitorTypeId: z.string().min(1).optional(),
    isActive: booleanQueryParamSchema.optional(),
});
/* -------------------------------------------------------------------------- */
/* (c) Admin — the resolved reads: what a kiosk WOULD render (FLOW-16)         */
/* -------------------------------------------------------------------------- */
/**
 * One tier resolution REFUSED on its way to the tier it served.
 *
 * This is the admin-visible half of a decision the kiosk deliberately never learns
 * about. Resolution does not fail for configuration an administrator can persist —
 * it degrades, tier by tier, and logs at `warn` (see `resolveEffectiveFlow` in the
 * visitor service, and the reasons enumerated there). That is the right behaviour for
 * a lobby and a terrible one for the person who just saved the flow: they see a
 * working terminal showing screens they did not configure, with nothing on any screen
 * of the console explaining why.
 *
 * So the preview says so. An empty `degradations` array means "what you are looking
 * at is your flow"; a non-empty one means "what you are looking at is a FALLBACK, and
 * here is the flow that was refused and why". It is the single most useful thing the
 * preview does, and it is why the preview goes through the resolver rather than
 * rendering the requested flow directly.
 *
 * `reason` is a snake_case machine key, the same string the
 * `VISITOR_FLOW_RESOLUTION_DEGRADED` log line carries, so an operator reading logs
 * and an admin reading the console are looking at the same vocabulary
 * (`selected_flow_unavailable`, `flow_has_no_renderable_screens`,
 * `flow_cannot_produce_a_visit`, `flow_failed_its_own_contract`). Deliberately a
 * `z.string()` and NOT an enum: a new refusal reason must be able to reach an admin
 * without a client redeploy, and this is an outbound schema (see the file note) — an
 * enum here would fail the whole preview for a reason it had not heard of, which is
 * the exact failure the degradation itself is reporting.
 *
 * `missingFieldKeys` is populated for `flow_cannot_produce_a_visit` and empty
 * otherwise: those keys ARE the fix, and naming them turns "your flow was rejected"
 * into "add a host question to it".
 */
export const flowResolutionDegradationSchema = z
    .object({
    /** The flow the refused tier resolved to — the row to fix. */
    flowId: z.string().min(1),
    /** The flow the tier NAMED, which differs from `flowId` when inheritance was folded. */
    assignedFlowId: z.string().min(1),
    source: flowResolutionSourceSchema,
    reason: z.string().min(1),
    /** Semantic keys a check-in needs that the refused flow never collects. */
    missingFieldKeys: z.array(z.string().min(1)),
})
    .passthrough();
/**
 * `GET /api/web/locations/:id/flow` — the flow in effect at one location right now,
 * WITH its provenance. `visitor.flow.read`.
 *
 * The same document `GET /api/device/v1/flows/effective` serves a kiosk at that
 * location, from the same resolver call, plus the three facts the kiosk is
 * deliberately not told: which inheritance rule fired (`source`), what the flow is
 * called (`flowName`), and which tiers were refused on the way (`degradations`).
 * `resolveEffectiveFlow` already returns all three — see `FLOW_RESOLUTION_SOURCES`,
 * whose whole reason for existing is this surface.
 *
 * `.passthrough()`, which this envelope is entitled to be where the document it wraps is
 * not: this is the resolver's own computed output rather than a row, so there is no
 * server-owned column for an allow-list to keep out (see the header's split) — and it is
 * shallow enough not to hit the `TS7056` ceiling the nested document does. The nested
 * `flow` is the kiosk contract unchanged: `resolvedCheckInFlowDTOSchema` itself, a plain
 * `z.object` for the two reasons annotated there, not a re-declaration of it.
 *
 * "Unbounded" WITH THE ONE EXCEPTION THIS ENVELOPE ACTUALLY CARRIES, because the earlier
 * version of this note claimed it had none while `locationId` was `z.string().min(1)`:
 * that `.min(1)` stays and is category 1 of the header's list — non-emptiness on an
 * identifier the SERVER echoes back from the request it just validated, so there is no
 * stored row that can violate it and nothing for an admin to fix if one could. Every
 * OTHER value here is unbounded, `flowName` conspicuously so (see below). A bound on any
 * of them would fail an admin's read of exactly the configuration they need to see.
 */
export const effectiveCheckInFlowDTOSchema = z
    .object({
    /**
     * The location resolved FOR, echoed back — not necessarily the flow's own scope.
     *
     * `.min(1)` is deliberate and is the only bound on this envelope: the server echoes
     * an id it resolved against, so this is a server-derived identifier, not stored data
     * being read back.
     */
    locationId: z.string().min(1),
    /** The visitor type resolution was asked for; `null` = the entry ("all types") flow. */
    visitorTypeId: z.string().min(1).nullable(),
    /** Which of the four inheritance tiers fired. */
    source: flowResolutionSourceSchema,
    /**
     * The admin-console name of the flow that was served. Never on the kiosk wire.
     *
     * UNBOUNDED, INCLUDING NO `.min(1)`, unlike `checkInFlowDTOSchema.name`. This is
     * the envelope around a resolved document, so a bound here fails the whole
     * preview — and the read is how an admin would DISCOVER that a flow was saved
     * with an empty name in the first place.
     */
    flowName: z.string(),
    /** Empty when the served flow is the one that was assigned. */
    degradations: z.array(flowResolutionDegradationSchema),
    flow: resolvedCheckInFlowDTOSchema,
})
    .passthrough();
/**
 * `GET /api/web/flows/:id/preview` — `flowPreviewQuerySchema` in, this out.
 * `visitor.flow.read`. Creates no `Visit` (FLOW-16).
 *
 * `flow` HERE IS BYTE-FOR-BYTE WHAT THE KIOSK AT `locationId` WOULD GET, including
 * `revision`, because it is produced by the same `resolveEffectiveFlow` call the
 * device route makes — the previewed flow is simply offered to the walk as its most
 * preferred candidate. An integration test asserts the two responses are equal; a
 * preview that diverges from the terminal is worse than no preview, because it is
 * trusted.
 *
 * That reuse is also what makes `degradations` meaningful: preview a flow that cannot
 * produce a check-in and you get the fallback a kiosk would get, WITH the refusal that
 * sent it there — the loud counterpart, on a surface an admin is actually looking at,
 * to a resolution path that must stay quiet.
 *
 * `previewLocale` is the locale the kiosk's fallback chain (requested ->
 * `defaultLocale` -> `en`) actually lands on for `requestedLocale`. When they differ,
 * the flow has no copy in the locale the admin asked for — which is a translation gap
 * they can fix, not an error.
 */
export const flowPreviewDTOSchema = effectiveCheckInFlowDTOSchema.extend({
    /** The flow the preview was asked for. Differs from `flow.flowId` exactly when a degradation fired. */
    requestedFlowId: z.string().min(1),
    requestedLocale: localeCodeSchema,
    previewLocale: localeCodeSchema,
});
/** One row of `GET /api/web/flows`. Scope and visitor-type binding are on the flow itself (S24). */
export const checkInFlowListItemDTOSchema = checkInFlowDTOSchema;
//# sourceMappingURL=check-in-flow.js.map